import socketio from "@feathersjs/socketio-client";
import io from "socket.io-client";
import makeDebug from "debug";
import { FeathersError, Timeout } from "@feathersjs/errors";
import { nanoid } from "nanoid";
import auth, { AuthenticationClient } from "@feathersjs/authentication-client";
import { StorageWrapper } from "@feathersjs/authentication-client/lib/storage";
import axios from "axios";
import { bindActionCreators, createAction, createAsyncThunk, createReducer, createSelector, createSlice } from "@reduxjs/toolkit";
import uniqBy from "lodash/uniqBy";
import uniqWith from "lodash/uniqWith";
import isEqual from "lodash/isEqual";
import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef } from "react";
import { differenceWith, isEqual as isEqual$1 } from "lodash";
import { ReactReduxContext, useSelector } from "react-redux";
import { jsx } from "react/jsx-runtime";
import memoize from "memoize-one";
import PropTypes from "prop-types";
export * from "@feathersjs/errors";
//#region \0rolldown/runtime.js
var __defProp = Object.defineProperty;
var __exportAll = (all, no_symbols) => {
	let target = {};
	for (var name in all) __defProp(target, name, {
		get: all[name],
		enumerable: true
	});
	if (!no_symbols) __defProp(target, Symbol.toStringTag, { value: "Module" });
	return target;
};
//#endregion
//#region src/selectors.ts
var EMPTY_ARRAY = [];
var DEFAULT_OPTIONS = Object.freeze({
	defaultPath: "default",
	storeAsProp: "storeAs"
});
var isNonEmptyString = (str) => typeof str === "string" && str.length && str.trim() === str;
/**
* Validates the context of selector for the given FeatherJS service and data field.
*
* @param {string} serviceName non-empty name the reduxified FeatherJS service
* @param {string} defaultPath (optional) non-empty name of the FeatherJS state field
* @returns {object} options for the selector being create.
* @throws {TypeError} if service name or storeAs can't be used to create selector
*/
function normalizeOptions(serviceName, defaultPath) {
	if (!isNonEmptyString(serviceName)) throw new TypeError("Invalid name of the service, non-empty string expected.");
	if (defaultPath === void 0 || defaultPath === null) return Object.freeze({
		...DEFAULT_OPTIONS,
		serviceName
	});
	if (isNonEmptyString(defaultPath)) return Object.freeze({
		...DEFAULT_OPTIONS,
		serviceName,
		defaultPath
	});
	throw new TypeError("Invalid context of the data, non-empty string expected.");
}
/**
* Get the root of FeatherJS service state from Redux.
*
* @param {State<T, U>} state root of Redux state
* @param {ComponentProps | undefined} props component own props
* @param {Options} options options of the created selector
* @returns {ServiceData<T, U> | undefined} value of "state.feathers.<serviceName>.<props.storeAs|defaultPath>"
*/
function getFeathersState(state, props, options) {
	const storePath = props?.[options.storeAsProp] || options.defaultPath;
	return state.feathers[options.serviceName]?.[storePath];
}
/**
* Creates new Redux selector "isLoading" for given FeathersJS service.
*
* It expects that root of the FeatherJS services is on "state.feathers" path.
* The service store path is expected to be "default" if not specified otherwise.
* The data field can be also overriden using the props with { storeAs: "custom" }.
*
* @param {string} service name of the FeathersJS service, e.g. "alphabet"
* @param {string} storeAs (optional) override of data field, e.g. "greek"
* @returns {BooleanFn<T, U>} isLoading(state) function that check loading state of service
* @example
* // A selector can be created using the service name only
* // ... and it will load from "default":
* const isLoading = feathersIsLoading("alphabet");
* // ... or using the override of field with data:
* const isLoadingSpecial = feathersIsLoading("alphabet", "special");
* // The selector allows to override the default path using props:
* const mapStateToProps = (state, ownProps) => ({
*     fromDefault: isLoading(state),
*     fromSpecial: isLoadingSpecial(state),
*     fromDynamic: isLoading(state, { storeAs: "dynamic" })
* });
*/
function feathersIsLoading(service, storeAs) {
	const opts = normalizeOptions(service, storeAs);
	return function isLoading(state, props) {
		return getFeathersState(state, props, opts)?.isLoading || false;
	};
}
function feathersIsSaving(service, storeAs) {
	const opts = normalizeOptions(service, storeAs);
	return function isSaving(state, props) {
		return getFeathersState(state, props, opts)?.isSaving || false;
	};
}
/**
* Create a redux selector which checks isLoading status of multiple services.
*
* @example
* const isLoading = feathersIsLoadingMultiple(["service1", "service2"]);
*
*/
function feathersIsLoadingMultiple(services, storeAs) {
	return function isLoadingMultiple(state, props) {
		return services.some((s) => feathersIsLoading(s, storeAs)(state, props));
	};
}
/**
* @deprecated this function was used in the past for services in custom (non-default) redux store
* because it is checking if the service state actually exists. This is no longer needed
* as any custom Redux store (via storeAs) comes initialized with the default state.
* 
* Also isFinished is binary complement of isLoading, so the condidition here does not
* do what most people think it does.
* It returns true when the service has never been called yet, or if it's currently loading data.
* 
* !!! It cannot be used as permanent indicator if the service data has been loaded at least once. !!!
* 
* Use feathersIsLoading instead.
*/
function feathersIsLoadingCustom(service, storeAs) {
	const opts = normalizeOptions(service, storeAs);
	return function isLoadingCustom(state, props) {
		const serviceState = getFeathersState(state, props, opts);
		return !serviceState || !serviceState.isFinished || serviceState.isLoading;
	};
}
/**
* Creates new Redux selector "getQueryResult" for given FeathersJS service.
*
* It expects that root of the FeatherJS services is on "state.feathers" path.
* The service store path is expected to be "default" if not specified otherwise.
* The data field can be also overriden using the props with { storeAs: "custom" }.
*
* So, the selector returns value of "state.feathers.serviceName.defaultPath.queryResult"
* unless overriden via props "state.feathers.serviceName.<props.storeAs>.queryResult".
*
* @param {string} service name of the FeathersJS service, e.g. "alphabet"
* @param {string} storeAs (optional) override of data field, e.g. "greek"
* @returns {ResultQueryFn<T, U>} getQueryResult(state) function that returns the data  of service
* @example
* // A selector can be created using the service name only
* // ... and it will load from "default":
* const getAlphabet = feathersGetQueryResult("alphabet");
* // ... or using the override of field with data:
* const getGreekAlphabet = feathersGetQueryResult("alphabet", "greek");
* // The selector allows to override the default path using props:
* const mapStateToProps = (state, ownProps) => ({
*     latin: getAlphabet(state),       // state.feathers.alphabet.default.queryResult
*     greek: getGreekAlphabet(state),  // state.feathers.alphabet.greek.queryResult
*     egypt: getAlphabet(state, { storeAs: "egypt" }), // state.feathers.alphabet.egypt.queryResult
* });
*/
function feathersGetQueryResult(service, storeAs) {
	const opts = normalizeOptions(service, storeAs);
	return function getQueryResult(state, props) {
		return getFeathersState(state, props, opts)?.queryResult;
	};
}
/**
* Creates new Redux selector "getServiceData" for given FeathersJS service.
*
* It expects that root of the FeatherJS services is on "state.feathers" path.
* The service store path is expected to be "default" if not specified otherwise.
* The data field can be also overriden using the props with { storeAs: "custom" }.
*
* It also expects that queryResult contains the paging result with nested data.
* So, the selector returns value of "state.feathers.serviceName.defaultPath.queryResult.data"
* unless overriden via props "state.feathers.serviceName.<props.storeAs>.queryResult.data".
*
* @param {string} service name of the FeathersJS service, e.g. "alphabet"
* @param {string} storeAs (optional) override of data field, e.g. "greek"
* @returns {ResultDataFn<T, U>} getServiceData(state) function that returns the data of service
* @example
* // A selector can be created using the service name only
* // ... and it will load from "default":
* const getAlphabet = feathersGetServiceData("alphabet");
* // ... or using the override of field with data:
* const getGreekAlphabet = feathersGetServiceData("alphabet", "greek");
* // The selector allows to override the default path using props:
* const mapStateToProps = (state, ownProps) => ({
*     latin: getAlphabet(state),       // state.feathers.alphabet.default.queryResult.data
*     greek: getGreekAlphabet(state),  // state.feathers.alphabet.greek.queryResult.data
*     egypt: getAlphabet(state, { storeAs: "egypt" }), // state.feathers.alphabet.egypt.queryResult.data
* });
*/
function feathersGetServiceData(service, storeAs) {
	const opts = normalizeOptions(service, storeAs);
	return function getServiceData(state, props) {
		return getFeathersState(state, props, opts)?.queryResult?.data || EMPTY_ARRAY;
	};
}
/**
* Creates new Redux selector "getSearchIndex" for given FeathersJS service.
* @param {string} service name of the FeathersJS service, e.g. "alphabet"
* @param {string} storeAs (optional) override of data field, e.g. "greek"
* @returns {ResultSearchIndexFn<T, U>} getSearchIndex(state) function that returns the data of service
* organized as map for the direct access by the item id
*/
function feathersGetSearchIndexData(service, storeAs) {
	const opts = normalizeOptions(service, storeAs);
	return function getSearchIndex(state, props) {
		return getFeathersState(state, props, opts)?.searchIndex;
	};
}
/**
* Return feathers service data - the value is populated as a result of get/create/patch/update/remove service
* call.
*
* For results of find() method @see feathersGetServiceData
*
* @param service name of the FeathersJS service as define in client
* @param storeAs (optional) override of default storage
* @returns
*/
function feathersGetSingleData(service, storeAs) {
	const opts = normalizeOptions(service, storeAs);
	return function getServiceData(state, props) {
		return getFeathersState(state, props, opts)?.data;
	};
}
/**
* Return feathers service isError object or null
* 
* @param service name of the FeathersJS service as define in client
* @param storeAs (optional) override of default storage
* @returns 
*/
function feathersIsError(service, storeAs) {
	const opts = normalizeOptions(service, storeAs);
	return function isError(state, props) {
		return getFeathersState(state, props, opts)?.isError;
	};
}
//#endregion
//#region src/subscriptions.ts
var debug$4 = makeDebug("feathers-client:subscriptions");
var subscriptionMap = /* @__PURE__ */ new Map();
/**
* Subscribe to specified serviceName (channel) to receive real-time updates.
*/
function subscribeFn(app) {
	return function(serviceName, options = {}) {
		const serviceSubscription = subscriptionMap.get(serviceName);
		options.uuid = nanoid();
		debug$4("[subscribe] Subscribing for %s with options %o", serviceName, options);
		if (serviceSubscription) {
			if (options.overrideSubscription) serviceSubscription.options = serviceSubscription.options.filter((o) => o.storeAs !== options.storeAs);
			serviceSubscription.options.push(options);
		} else subscriptionMap.set(serviceName, {
			serviceName,
			options: [options]
		});
		app.service("subscribe").create({
			serviceName,
			...options.subscribeFor && { where: options.subscribeFor },
			uuid: options.uuid
		}).catch((error) => {
			console.error(error);
		});
	};
}
/**
* Unsubscribe from specified path (channel) to stop receiving real-time updates.
*/
function unsubscribeFn(app) {
	return function(serviceName, options = {}) {
		const serviceSubscription = subscriptionMap.get(serviceName);
		if (serviceSubscription && serviceSubscription.options.find((o) => o === options)) {
			debug$4("[unsubscribe] Unsubscribing from %s with options %o", serviceName, options);
			const leftSubscriptions = serviceSubscription.options.filter((o) => o !== options);
			if (leftSubscriptions.length === 0) subscriptionMap.delete(serviceName);
			else subscriptionMap.set(serviceName, {
				serviceName,
				options: leftSubscriptions
			});
			if (app.io.connected) app.service("unsubscribe").create({
				serviceName,
				...options.subscribeFor && { where: options.subscribeFor },
				uuid: options.uuid
			}).catch((error) => {
				if (app.io.connected || !(error instanceof Timeout)) console.error(error);
			});
			return true;
		} else {
			debug$4("[unsubscribe] No subscription found for %s with options %o", serviceName, options);
			return false;
		}
	};
}
/**
* Add a new ID into service subscription. Use case is when component is subscribed
* only to certain IDs (ie Kit list) and new Kit is created. No event is received from the server,
* client must update it's redux state with new data itself. But it also must add this new ID into subscription
* condition in order to receive updates for this new item.
*/
var addIdToSubscriptionFn = (app) => (serviceName, id, storeAs) => {
	const serviceSubscription = subscriptionMap.get(serviceName);
	const subscription = serviceSubscription && serviceSubscription.options.find((o) => o.storeAs === storeAs);
	if (subscription) {
		if (!Array.isArray(subscription.subscribeFor) && subscription.subscribeFor?.id && Array.isArray(subscription.subscribeFor?.id)) {
			subscription.subscribeFor.id.push(id);
			app.service("subscribe").create({
				serviceName,
				where: subscription.subscribeFor,
				uuid: subscription.uuid
			}).catch((error) => {
				console.error(error);
			});
		}
	}
};
/**
* Check if client is already subscribed to the service with specified storeAs (or default).
* It does not check if the subscription has the same condition, only that it exists.
* @param serviceName
* @param storeAs
* @returns
*/
var alreadyHasSubscription = (serviceName, storeAs) => {
	const subscription = subscriptionMap.get(serviceName);
	return subscription !== null && subscription?.options.some((s) => s.storeAs === storeAs);
};
/**
* Unsubscribe from all currently active subscriptions and clear the subscription map.
* Must be called BEFORE a token/user switch (e.g. impersonation) so that all unsubscribe
* requests reach the server while the old token is still active.
*/
function unsubscribeAllFn(app) {
	return function() {
		if (!app.io.connected) {
			subscriptionMap.clear();
			return;
		}
		subscriptionMap.forEach(({ serviceName, options }) => {
			options.forEach((option) => {
				app.service("unsubscribe").create({
					serviceName,
					...option.subscribeFor && { where: option.subscribeFor },
					uuid: option.uuid
				}).catch((error) => {
					if (app.io.connected || !(error instanceof Timeout)) console.error(error);
				});
			});
		});
		subscriptionMap.clear();
	};
}
/**
* Get the values of enum entries.
*
* @param object enum definition, e.g. enum Direction { UP = "up", DOWN = "down" }
* @returns array of names, e.g. [ "up", "down" ]
*/
function getEnumValues(object) {
	const result = new Array();
	for (const name in object) if (Number.isNaN(Number(name))) result.push(object[name]);
	return result;
}
//#endregion
//#region ../../spork-common/src/defs/Features.ts
/**
* Feature flag definitions supported by SPORK.
*/
var FeatureFlag = /* @__PURE__ */ function(FeatureFlag) {
	FeatureFlag["STUDENT_JOIN_RULES"] = "student-join-rules";
	FeatureFlag["VERSION_FOR_KIDS"] = "version-for-kids";
	FeatureFlag["COMPREHENSIVE_ASSESSMENT"] = "comprehensive-assessment";
	FeatureFlag["OCI_CLASSES"] = "oci-classes";
	FeatureFlag["TOPIC_COLLECTION"] = "topic-collection";
	FeatureFlag["FREE_IPLANNER"] = "free-iplanner";
	FeatureFlag["MANUAL_LESSON_COMPLETION"] = "manual-lesson-completion";
	FeatureFlag["COURSE_MENTORING"] = "course-mentoring";
	FeatureFlag["AUTHORING"] = "authoring";
	FeatureFlag["DATA_MANAGEMENT"] = "data-management";
	FeatureFlag["ASSESSMENT_BANKS"] = "assessment-banks";
	FeatureFlag["HIDE_DEVICE_PROVISIONING"] = "hide-device-provisioning";
	FeatureFlag["SHOW_STATS"] = "show-stats";
	FeatureFlag["SHOW_MICROLEARNING"] = "show-microlearning";
	FeatureFlag["INTERACTIVE_QUIZZES"] = "interactive-quizzes";
	FeatureFlag["STUDENT_CHECKIN"] = "student-checkin";
	FeatureFlag["CAN_COMMENT_KIT_LESSON"] = "can-comment-kit-lesson";
	FeatureFlag["GRADEBOOKS"] = "gradebooks";
	FeatureFlag["DISTANCE_SUPPORT"] = "distance-support";
	FeatureFlag["HIDE_CURRENT_KITS"] = "hide-current-kits";
	FeatureFlag["SECTION_TEACHER_MODES"] = "section-teacher-modes";
	FeatureFlag["LESSON_AUTOSAVE"] = "lesson-autosave";
	return FeatureFlag;
}({});
/**
* Feature restriction specific to SPORK entity.
*
* The feature may be configured for the whole tenant or for specific entity.
*/
var FeatureByEntity = /* @__PURE__ */ function(FeatureByEntity) {
	FeatureByEntity["SCHOOL"] = "schools";
	return FeatureByEntity;
}({});
getEnumValues(FeatureFlag);
getEnumValues(FeatureByEntity);
var FEATURE_SELECTOR_KEY = { ["schools"]: "school_id" };
/**
* Checks whether activeKeys includes a single dataKey or all given dataKeys.
*
* @param activeKeys configured
* @param context single value or array of values to match with the configuration
* @returns true if single key, all given keys
*/
function isKeyIncluded(activeKeys, context) {
	if (Array.isArray(context)) return context.length > 0 && context.every((item) => activeKeys.indexOf(item) !== -1);
	return activeKeys.includes(context);
}
/**
* Checks whether the current application context matches with the configuration.
*
* @param configured the feature enabled entities (a.k.a. featureSelectors)
* @param context app context we are checking against (a.k.a. selectorData)
* @returns true if the feature is active for given context
*/
function isFeatureContextMatching(configured, context) {
	const featureEntities = configured && Object.keys(configured);
	if (!featureEntities || featureEntities.length === 0) return true;
	if (!context || Object.keys(context).length === 0) return false;
	for (const entity of featureEntities) {
		const contextKey = FEATURE_SELECTOR_KEY[entity];
		const contextValue = contextKey && context[contextKey];
		if (!contextValue || !isKeyIncluded(configured[entity], contextValue)) return false;
	}
	return true;
}
var isDataMatchingSelector = isFeatureContextMatching;
//#endregion
//#region ../../spork-common/src/problems/const.ts
/**
* Constants for various types of Problems (questions)
*/
var PROBLEM_TYPE = /* @__PURE__ */ function(PROBLEM_TYPE) {
	PROBLEM_TYPE["OPEN_ANSWER"] = "oa";
	PROBLEM_TYPE["MULTIPLE_CHOICE"] = "mc";
	PROBLEM_TYPE["MULTIPLE_ANSWER"] = "mx";
	PROBLEM_TYPE["TRUE_FALSE"] = "tf";
	PROBLEM_TYPE["MATCHING"] = "ma";
	PROBLEM_TYPE["TESTLET"] = "pa";
	PROBLEM_TYPE["SORTING"] = "so";
	return PROBLEM_TYPE;
}({});
var PROBLEM_TYPE_NAME = {
	["oa"]: "Open Answer",
	["mc"]: "Multiple Choice",
	["mx"]: "Multiple Answer Correct",
	["tf"]: "True or False",
	["ma"]: "Matching",
	["pa"]: "Testlet",
	["so"]: "Sorting"
};
Object.keys(PROBLEM_TYPE_NAME).map((key) => ({
	key,
	value: key,
	text: PROBLEM_TYPE_NAME[key]
}));
Object.entries({
	["C"]: "c",
	["PC"]: "pc"
}).reduce((acc, [k, v]) => {
	acc[v] = k;
	return acc;
}, {});
var COMP_TEST_STATUS = /* @__PURE__ */ function(COMP_TEST_STATUS) {
	COMP_TEST_STATUS["NEW"] = "New";
	COMP_TEST_STATUS["UC"] = "UC";
	COMP_TEST_STATUS["READY"] = "Ready";
	COMP_TEST_STATUS["LOCKED"] = "Locked";
	return COMP_TEST_STATUS;
}({});
PROBLEM_TYPE.MULTIPLE_ANSWER, PROBLEM_TYPE.MULTIPLE_CHOICE, PROBLEM_TYPE.OPEN_ANSWER, PROBLEM_TYPE.TRUE_FALSE, PROBLEM_TYPE.MATCHING, PROBLEM_TYPE.SORTING, PROBLEM_TYPE.MULTIPLE_CHOICE;
//#endregion
//#region ../../spork-common/src/const.ts
/**
* Defines Teacher status in relation to School Course.
*/
var TEACHER_STATUS = /* @__PURE__ */ function(TEACHER_STATUS) {
	TEACHER_STATUS["INDEPENDENT"] = "I";
	TEACHER_STATUS["FOLLOWER"] = "F";
	return TEACHER_STATUS;
}({});
//#endregion
//#region ../../spork-common/src/element/constants.ts
/**
* Status of the Element (aTiP, TiP, iPlanner)
*/
var ELEMENT_STATUS = /* @__PURE__ */ function(ELEMENT_STATUS) {
	ELEMENT_STATUS["DRAFT"] = "D";
	ELEMENT_STATUS["FOR_REVIEW"] = "FR";
	ELEMENT_STATUS["READY"] = "R";
	ELEMENT_STATUS["PUBLISHED"] = "P";
	ELEMENT_STATUS["SUSPENDED"] = "S";
	return ELEMENT_STATUS;
}({});
TEACHER_STATUS.FOLLOWER, TEACHER_STATUS.INDEPENDENT;
//#endregion
//#region ../../spork-common/src/auth/roleConsts.ts
var ROLE = /* @__PURE__ */ function(ROLE) {
	ROLE["ADMIN"] = "ROLE_ADMIN";
	ROLE["SPORK_COORDINATOR"] = "ROLE_SPORK_COORDINATOR";
	ROLE["SUPERAUTHOR"] = "ROLE_SUPERAUTHOR";
	ROLE["PACKAGE_ADMIN"] = "ROLE_PACKAGE_ADMIN";
	ROLE["CONTENT_COORDINATOR"] = "ROLE_CONTENT_COORDINATOR";
	ROLE["ASSESSMENT_ADMIN"] = "ROLE_ASSESSMENT_ADMIN";
	ROLE["TENANT_COORDINATOR"] = "ROLE_TENANT_COORDINATOR";
	ROLE["SCHOOL_ADMIN"] = "ROLE_SCHOOL_ADMIN";
	ROLE["SCHOOL_REGISTRAR"] = "ROLE_SCHOOL_REGISTRAR";
	ROLE["SCHOOL_ASSESSMENT_MANAGER"] = "ROLE_SCHOOL_ASSESSMENT_MANAGER";
	ROLE["SCHOOL_ASSIGNMENT_REVIEWER"] = "ROLE_SCHOOL_ASSIGNMENT_REVIEWER";
	ROLE["TENANT_REGISTRAR"] = "ROLE_TENANT_REGISTRAR";
	ROLE["BLOCK_ADMIN"] = "ROLE_BLOCK_ADMIN";
	ROLE["SUPER_MENTOR"] = "ROLE_SUPER_MENTOR";
	ROLE["MENTOR"] = "ROLE_MENTOR";
	ROLE["AUTHORING_SUPER_MENTOR"] = "ROLE_AUTHORING_SUPER_MENTOR";
	ROLE["AUTHORING_MENTOR"] = "ROLE_AUTHORING_MENTOR";
	ROLE["PACKAGE_PROBLEM_EDITOR"] = "ROLE_PACKAGE_PROBLEM_EDITOR";
	ROLE["TEST_WRITER"] = "ROLE_TEST_WRITER";
	ROLE["TABLET_ADMIN"] = "ROLE_TABLET_ADMIN";
	ROLE["SCHOOL_TABLET_ADMIN"] = "ROLE_SCHOOL_TABLET_ADMIN";
	ROLE["SUPERSAD"] = "ROLE_SUPERSAD";
	ROLE["QAD"] = "ROLE_QAD";
	ROLE["COMP_QUESTIONS_EDITOR"] = "ROLE_COMP_QUESTIONS_EDITOR";
	ROLE["RESOURCE_ADMIN"] = "ROLE_RESOURCE_ADMIN";
	ROLE["AUTHOR"] = "ROLE_AUTHOR";
	ROLE["REVIEWER"] = "ROLE_REVIEWER";
	ROLE["COMMENT_RECIPIENT"] = "ROLE_COMMENT_RECIPIENT";
	ROLE["USER"] = "ROLE_USER";
	ROLE["NONE"] = "ROLE_NONE";
	ROLE["IMPERSONATOR"] = "ROLE_IMPERSONATOR";
	ROLE["SURVEY_ADMIN"] = "ROLE_SURVEY_ADMIN";
	ROLE["MICROLEARNING_ADMIN"] = "ROLE_MICROLEARNING_ADMIN";
	ROLE["GRADE_SCALE_ADMIN"] = "ROLE_GRADE_SCALE_ADMIN";
	return ROLE;
}({});
/**
* Definition of objects for roles to be assigned to.
*/
var ROLE_ENTITY_TYPE = /* @__PURE__ */ function(ROLE_ENTITY_TYPE) {
	ROLE_ENTITY_TYPE["APPLICATION"] = "APPLICATION";
	ROLE_ENTITY_TYPE["TENANT"] = "TENANT";
	ROLE_ENTITY_TYPE["KIT"] = "KIT";
	ROLE_ENTITY_TYPE["PACKAGE"] = "PACKAGE";
	ROLE_ENTITY_TYPE["BLOCK"] = "BLOCK";
	ROLE_ENTITY_TYPE["COURSE"] = "COURSE";
	ROLE_ENTITY_TYPE["ATIP"] = "AUTHORING_TIP";
	ROLE_ENTITY_TYPE["SCHOOL"] = "SCHOOL";
	ROLE_ENTITY_TYPE["TEST"] = "TEST";
	return ROLE_ENTITY_TYPE;
}({});
var ROLES_BLOCK = ["ROLE_BLOCK_ADMIN"];
var ROLES_COURSE = [
	"ROLE_SUPER_MENTOR",
	"ROLE_MENTOR",
	"ROLE_TEST_WRITER"
];
var ROLES_KIT = [
	"ROLE_SUPERAUTHOR",
	"ROLE_AUTHOR",
	"ROLE_REVIEWER",
	"ROLE_COMMENT_RECIPIENT",
	"ROLE_USER"
];
var ROLES_PACKAGE = ["ROLE_PACKAGE_ADMIN", "ROLE_PACKAGE_PROBLEM_EDITOR"];
var ROLES_TENANT = [
	"ROLE_ADMIN",
	"ROLE_USER",
	"ROLE_TABLET_ADMIN",
	"ROLE_TENANT_COORDINATOR",
	"ROLE_CONTENT_COORDINATOR",
	"ROLE_ASSESSMENT_ADMIN",
	"ROLE_IMPERSONATOR",
	"ROLE_RESOURCE_ADMIN",
	"ROLE_TENANT_REGISTRAR",
	"ROLE_COMP_QUESTIONS_EDITOR",
	"ROLE_SURVEY_ADMIN",
	"ROLE_MICROLEARNING_ADMIN",
	"ROLE_GRADE_SCALE_ADMIN"
];
var ROLES_ATIP = ["ROLE_AUTHORING_MENTOR", "ROLE_AUTHORING_SUPER_MENTOR"];
//#endregion
//#region ../../spork-common/src/auth/roleUtils.ts
var isUserIdEqualTo = (matchTo) => {
	const field = typeof matchTo === "string" && matchTo.trim();
	if (!field) throw new Error(`Invalid name of field to match (${String(matchTo)})`);
	return (context) => {
		const userId = context && context.userId;
		return userId ? userId === context[field] : false;
	};
};
var isUserIdOneOf = (matchTo) => {
	const field = typeof matchTo === "string" && matchTo.trim();
	if (!field) throw new Error(`Invalid name of field to match (${String(matchTo)})`);
	return (context) => {
		const userId = context && context.userId;
		const idArray = context[field];
		return userId ? Array.isArray(idArray) && idArray.some((id) => userId === id) : false;
	};
};
var isUserAuthor = isUserIdEqualTo("authorId");
var isUserLocker = isUserIdEqualTo("lockedBy");
var isUserTeacher = isUserIdEqualTo("teacherId");
var isUserCommentOwner = isUserIdEqualTo("commentOwnerId");
var isUserResourceOwner = isUserIdEqualTo("created_by");
var isUserOneOfTeachers = isUserIdOneOf("teachers");
var isUserOneOfSubstitutes = isUserIdOneOf("substitutes");
var is_cloneable = (context) => Boolean(context.is_cloneable && context.is_valid);
/**
* Check membership in given school.
*
* @property {array} memberOfSchools Array of school IDs where the current user is a member.
* @property {number} school_id School ID to check.
* @returns {boolean}
*/
var isMemberOfSchool = ({ memberOfSchools, school_id }) => memberOfSchools && school_id ? memberOfSchools.includes(school_id) : false;
/**
* Question is not assigned to Ready or Locked test
* @property {string[]|null|undefined}  testStatuses - status names array
* of all tests the question is assigned to
* @returns {boolean}
*/
var questionIsEditable = (testStatuses) => !testStatuses || testStatuses.length === 0 || !testStatuses.some((s) => s === COMP_TEST_STATUS.READY || s === COMP_TEST_STATUS.LOCKED);
/**
* Returns value of `is_active` property (if exists = iPlanner) from passed data, otherwise true (ATiP, TiP).
* This property is not used for ATiPs and TiPs, it is detected by `undefined`.
*
* @property {boolean|undefined} is_active iPlanner property (see IPlanner.model).
* @returns {boolean}
*/
var isActiveIPlanner = ({ is_active }) => is_active === void 0 || is_active;
//#endregion
//#region ../../spork-common/src/auth/roles/baseRole.ts
function checkPrimitive(id, expectedId) {
	return expectedId === String(id);
}
function checkArray(ids, expectedId) {
	return ids.some((id) => expectedId === String(id));
}
var RoleDefinition = class {
	roleName = ROLE.NONE;
	assignedEntityId;
	name;
	plural;
	entityType;
	dynamic;
	dynamicPrivate;
	static;
	staticPrivate;
	useToLimitDataFind;
	isHiddenFromRoleList;
	isHiddenForAssignmentFn;
};
var BaseRole = class extends RoleDefinition {
	isUserIdEqualTo = function(matchTo) {
		const field = typeof matchTo === "string" && matchTo.trim();
		if (!field) throw new Error(`Invalid name of field to match (${String(matchTo)})`);
		return (context) => {
			const userId = context && context.userId;
			return userId ? userId === context[field] : false;
		};
	};
	/**
	* Check if roleModel has role on object.
	* model - object which defines what to check in object against role
	*      { assignedEntityId: "tenant_id",
	*        roleName: "ROLE_ADMIN",
	*        entityType: "TENANT"
	*      }
	* role - user role assigned to user in database
	*      { role: "ROLE_ADMIN", entity_type: "TENANT", entity_id : "1" }
	*
	* object - object to determine user permission to
	*      { tenant_id: 1 , package_id: [1,2], kit_id: [] }
	*
	* returns true if object contains proper attribute as determined by roleModel with value from role
	*/
	accessValidator = function(roleModel, role, object) {
		if (!role || !object) return false;
		if (!roleModel.assignedEntityId) return false;
		const idToCheck = object[roleModel.assignedEntityId];
		if (!idToCheck) return false;
		return role.role === roleModel.roleName && Array.isArray(idToCheck) ? checkArray(idToCheck, role.entity_id) : checkPrimitive(idToCheck, role.entity_id) && role.entity_type === roleModel.entityType;
	};
	/**
	* Check if roleModel has role on object.
	* model - object which defines what to check in object against role
	*      { assignedEntityId: "tenant_id",
	*        roleName: "ROLE_ADMIN",
	*        entityType: "TENANT"
	*      }
	* role - user role assigned to user in database
	*      { role: "ROLE_ADMIN", entity_type: "TENANT", entity_id : "1" }
	*
	* object - object to determine user permission to
	*      { tenant_id: 1 , package_id: [1,2], kit_id: [] }
	*
	* returns true if object contains proper attribute as determined by roleModel with value from role
	* OR if the object does not contain the attribute at all
	*/
	accessValidatorWithFreeForAll = function(roleModel, role, object) {
		if (!role || !object) return false;
		if (!roleModel.assignedEntityId) return false;
		const idToCheck = object[roleModel.assignedEntityId];
		return role.role === roleModel.roleName && Array.isArray(idToCheck) ? idToCheck.length === 0 || checkArray(idToCheck, role.entity_id) : idToCheck === null || checkPrimitive(idToCheck, role.entity_id) && role.entity_type === roleModel.entityType;
	};
	/**
	* Check if roleModel has role on object.
	* model - object which defines what to check in object against role
	*      { assignedEntityId: "comp_test_id",
	*        roleName: "ROLE_QAD",
	*        entityType: "TEST"
	*      }
	* role - user role assigned to user in database
	*      { role: "ROLE_QAD", entity_type: "TEST", entity_id : "123" }
	*
	* object - object to determine user permission to
	*      { tenant_id: 1 , comp_test_id: [] }
	*
	* returns true if the object does not contains proper attribute as determined by roleModel
	*/
	accessValidatorOnlyFreeForAll = function(roleModel, role, object) {
		if (!role || !object) return false;
		if (!roleModel.assignedEntityId) return false;
		const idToCheck = object[roleModel.assignedEntityId];
		return role.role === roleModel.roleName && Array.isArray(idToCheck) ? idToCheck.length === 0 : idToCheck === null;
	};
	/**
	* Check if there is intersection between input values or one of input value is empty
	* @param {number[]} userAccessibleIds - list of ID for which the user have rights
	*                                for example list of block_id where user have some rights
	* @param {number[]} objectIds  - object ID or list of IDs releated to validating object
	*
	* returns true if there is intersection in input values or objectIds is empty
	*/
	accessValidatorIntersectionOrEmpty = function(userAccessibleIds, objectIds) {
		if (!objectIds || Array.isArray(objectIds) && 0 === objectIds.length) return true;
		else if (!userAccessibleIds || 0 === userAccessibleIds.length) return false;
		else return userAccessibleIds.some((uId) => Array.isArray(objectIds) ? objectIds.some((oId) => oId === uId) : uId === objectIds);
	};
};
/**
* Created a new role based on `predecessor`.
* @param {object} predecessor
* @param {object} properties Properties to override / expand the properties inherited from the predecessor.
* @param {string} properties.name e.g. "Author"
* @param {string} properties.plural e.g. "Authors"
* @param {string} properties.roleName e.g. ROLE.AUTHOR
* @param {string} properties.entityType e.g. "KIT"
* @param {string} properties.assignedEntityId e.g. "kit_id"
* @param {array}  properties.static Inherited static properties will be expanded by this array.
* @param {object} properties.dynamic Inherited dynamic properties will be expanded by this object.
* @param {array}  properties.staticPrivate  These properties will not be inherited when
*                                           this role will be used as a predecessor
*                                           but will be used for evaluation static rules of this role.
* @param {array}  properties.dynamicPrivate These properties will not be inherited when
*                                           this role will be used as a predecessor
*                                           but will be used for evaluation dynamic rules of this role.
*
*/
function inheritRole(predecessor, properties) {
	const parentModel = { ...predecessor };
	const { static: staticRules, dynamic, ...props } = properties;
	delete parentModel.staticPrivate;
	delete parentModel.dynamicPrivate;
	const newModel = {
		...parentModel,
		...props
	};
	if (staticRules) newModel.static = [...new Set([...newModel.static || [], ...staticRules])];
	if (dynamic) newModel.dynamic = {
		...newModel.dynamic || {},
		...dynamic
	};
	return newModel;
}
//#endregion
//#region ../../spork-common/src/auth/roles/reviewer.ts
function createReviewer() {
	const model = inheritRole(new BaseRole(), {
		name: "Reviewer",
		plural: "Reviewers",
		roleName: ROLE.REVIEWER,
		entityType: "KIT",
		assignedEntityId: "kit_id",
		useToLimitDataFind: true,
		static: [
			"syllabi:visit",
			"sipiTemplates:visit",
			"problems:visit",
			"course:read",
			"block:read",
			"problemSet:visit",
			"aTiP:visit",
			"aKits-authoring:visit"
		],
		dynamic: {
			"aTiPComment:create": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"aTiPComment:delete": isUserCommentOwner,
			"aTiPComment:edit": isUserCommentOwner,
			"aTiP:view": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"aKit:review": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"problemSet:review": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"problemSet:view": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"problem:review": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			}
		}
	});
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/author.ts
function createAuthor() {
	const model = inheritRole(createReviewer(), {
		name: "Author",
		plural: "Authors",
		roleName: ROLE.AUTHOR,
		entityType: "KIT",
		assignedEntityId: "kit_id",
		static: [
			"problem:view",
			"problem:create",
			"problem:author",
			"problemSet:create",
			"lock:lock",
			"widgets:visit",
			"topics:visit",
			"widget:create",
			"widget:review",
			"glossary:create",
			"image:create",
			"image:manage",
			"clipart:create",
			"dndResource:manage"
		]
	});
	model.dynamic = {
		"aTiPComment:create": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"aTiPComment:delete": isUserCommentOwner,
		"aTiPComment:edit": isUserCommentOwner,
		"aTiP:view": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"kitComment:edit": (context) => model.isUserIdEqualTo("commentOwnerId")(context),
		"kitComment:delete": (context) => model.isUserIdEqualTo("commentOwnerId")(context),
		"lock:unlock": (context) => model.isUserIdEqualTo("lockedBy")(context),
		"glossary:manage": function(data, role, roleModel) {
			return model.accessValidatorWithFreeForAll(roleModel, role, data);
		},
		"problem:delete": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data) || isUserAuthor(data);
		},
		"problem:update": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data) || isUserAuthor(data);
		},
		"aKit:update": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"widget:update": function(data, role, roleModel) {
			return model.accessValidatorWithFreeForAll(roleModel, role, data);
		},
		"widget:delete": function(data, role, roleModel) {
			return model.accessValidatorWithFreeForAll(roleModel, role, data);
		},
		"aKit:publishPreview": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"problemSet:manage": function(data, role, roleModel) {
			return model.accessValidatorWithFreeForAll(roleModel, role, data) || isUserAuthor(data);
		},
		"problemSet:view": function(data, role, roleModel) {
			return model.accessValidatorWithFreeForAll(roleModel, role, data) || isUserAuthor(data);
		}
	};
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/qad.ts
function createQad() {
	const model = inheritRole(new BaseRole(), {
		name: "Question Advisor",
		plural: "Question Advisors",
		roleName: ROLE.QAD,
		entityType: ROLE_ENTITY_TYPE.TEST,
		assignedEntityId: "comp_test_id",
		useToLimitDataFind: false,
		static: [
			"compQuestion:visit",
			"compQuestion:create",
			"nativeTest:visit",
			"nativeTest:view",
			"lock:lock",
			"course:read",
			"block:read",
			"compProposal:load",
			"compProposal:visit"
		],
		dynamic: {
			"lock:unlock": isUserLocker,
			"compQuestion:manage": function(data, role, roleModel) {
				return model.accessValidatorOnlyFreeForAll(roleModel, role, data) && model.accessValidatorIntersectionOrEmpty(data.myBlockIDs, data.block_id);
			},
			"compQuestion:remove": function(data, role, roleModel) {
				return model.accessValidatorOnlyFreeForAll(roleModel, role, data) && model.accessValidatorIntersectionOrEmpty(data.myBlockIDs, data.block_id);
			},
			"compQuestion:view": function(data) {
				return model.accessValidatorIntersectionOrEmpty(data.myBlockIDs, data.block_id);
			},
			"compQuestion:review": function(data, role, roleModel) {
				return model.accessValidatorOnlyFreeForAll(roleModel, role, data) && model.accessValidatorIntersectionOrEmpty(data.myBlockIDs, data.block_id);
			},
			"compProposal:manage": function(data) {
				return model.accessValidatorIntersectionOrEmpty(data.myBlockIDs, data.block_id) && data.newStatus !== "Draft" && data.status !== "Draft";
			}
		}
	});
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/compQuestionsEditor.ts
function createCompQuestionsEditor() {
	const model = inheritRole(new BaseRole(), {
		name: "Comp Questions Editor",
		plural: "Comp Questions Editor",
		roleName: ROLE.COMP_QUESTIONS_EDITOR,
		entityType: ROLE_ENTITY_TYPE.TENANT,
		assignedEntityId: "tenant_id",
		useToLimitDataFind: true,
		static: [
			"block:read",
			"course:read",
			"compQuestion:visit",
			"compQuestion:view",
			"compTest:visit",
			"compTest:view"
		],
		dynamic: {
			"compQuestion:manage": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"compTest:correctQuestions": function(data, role, roleModel) {
				return data.status === COMP_TEST_STATUS.UC && model.accessValidator(roleModel, role, data);
			}
		}
	});
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/superauthor.ts
function createSuperAuthor() {
	const model = inheritRole(createAuthor(), {
		name: "Super Author",
		plural: "Super Authors",
		roleName: ROLE.SUPERAUTHOR,
		entityType: "KIT",
		assignedEntityId: "kit_id",
		static: [
			"maintenance:visit",
			"kitComment:edit",
			"kitComment:delete",
			"kitComment:resolve",
			"course:visit",
			"topicCollection:sync",
			"topicCollection:manage",
			"lock:unlock",
			"publishedContent:view",
			"aTiP:visit",
			"aTiP:view",
			"aTiP:createFromKit"
		],
		dynamic: {
			"aTiP:grant": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data) && data.grantRole !== void 0 && ROLES_ATIP.includes(data.grantRole);
			},
			"aKit:manage": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"aKit:review": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"aKit:publishRelease": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"aKit:sync": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"aKit:grant": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data) && data.grantRole !== void 0 && ROLES_KIT.includes(data.grantRole);
			},
			"problemSet:review": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"problem:review": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"lessonPath:manage": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			}
		}
	});
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/aMentor.ts
function createAuthoringMentor() {
	const model = inheritRole(new BaseRole(), {
		name: "Authoring Mentor",
		plural: "Authoring Mentors",
		roleName: ROLE.AUTHORING_MENTOR,
		entityType: "AUTHORING_TIP",
		assignedEntityId: "atip_id",
		useToLimitDataFind: true,
		static: [
			"problem:view",
			"problem:create",
			"problems:owned",
			"problemSet:create",
			"aTiP:visit",
			"aTiP:view",
			"widgets:visit",
			"aKits-authoring:visit",
			"myMessages:visit",
			"image:create",
			"aTiPComment:resolve",
			"clipart:create",
			"dndResource:manage"
		],
		dynamic: {
			"problem:delete": function(data) {
				return isUserAuthor(data);
			},
			"problem:update": function(data) {
				return isUserAuthor(data);
			},
			"problemSet:manage": function(data) {
				return isUserAuthor(data);
			},
			"problemSet:view": function(data) {
				return isUserAuthor(data);
			},
			"quizSiPi:manage": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"aTiP:manage": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"aTiP:clone": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"aTiP:grant": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data) && Boolean(data.canRevoke);
			},
			"aTiPComment:delete": isUserCommentOwner,
			"aTiPComment:edit": isUserCommentOwner,
			"lessonPath:manage": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"resource:edit": function(data) {
				return isUserResourceOwner(data) && Boolean(data.is_valid);
			},
			"resource:editContent": function(data) {
				return isUserResourceOwner(data) && !data.used_by_another_user && Boolean(data.is_valid);
			},
			"resource:clone": is_cloneable,
			"resource:delete": function(data) {
				return isUserResourceOwner(data) && !data.is_used;
			}
		}
	});
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/aSuperMentor.ts
function createAuthoringSuperMentor() {
	const model = inheritRole(createAuthoringMentor(), {
		name: "Authoring Super Mentor",
		plural: "Authoring Super Mentors",
		roleName: ROLE.AUTHORING_SUPER_MENTOR,
		entityType: "AUTHORING_TIP",
		assignedEntityId: "atip_id",
		dynamic: { "aTiP:grant": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data) && data.grantRole !== void 0 && ([ROLE.AUTHORING_MENTOR].includes(data.grantRole) || Boolean(data.canRevoke));
		} }
	});
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/packageAdmin.ts
function createPackageAdmin() {
	const aSuperMentor = createAuthoringSuperMentor();
	const superAuthor = createSuperAuthor();
	const model = inheritRole(new BaseRole(), {
		name: "Package Admin",
		plural: "Package Admins",
		roleName: ROLE.PACKAGE_ADMIN,
		entityType: "PACKAGE",
		assignedEntityId: "package_id",
		useToLimitDataFind: true
	});
	model.static = [...new Set([
		...aSuperMentor.static,
		...superAuthor.static,
		"aKit:create",
		"modules:visit",
		"template:create",
		"widgets:visit:extended",
		"problemSet:to-kit",
		"aTiP:createEmpty",
		"clipart:create",
		"dndResource:manage",
		"topicCollection:sync",
		"topicCollection:manage",
		"snippet:create"
	])];
	model.dynamic = {
		...aSuperMentor.dynamic,
		...superAuthor.dynamic,
		"package:manage": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"package:grant": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data) && data.grantRole !== void 0 && ROLES_PACKAGE.includes(data.grantRole);
		},
		"template:manage": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"lessonPath:manage": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"snippet:manage": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		}
	};
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/mentor.ts
var isTeacherTest = (data) => {
	const type = data && data.psetType && data.psetType.toLowerCase();
	return type !== void 0 && type === "tt";
};
function createMentor() {
	const model = inheritRole(new BaseRole(), {
		name: "Mentor",
		plural: "Mentors",
		roleName: ROLE.MENTOR,
		entityType: "COURSE",
		assignedEntityId: "course_variant_id",
		useToLimitDataFind: true,
		static: [
			"syllabi:visit",
			"sipiTemplates:visit",
			"myMessages:visit",
			"sipiTemplatesComment:resolve",
			"syllabiComment:resolve",
			"schoolCalendar:read",
			"ipCalendar:read",
			"course:read",
			"course:visit",
			"block:read",
			"sipiTemplates:tipCreate",
			"syllabi:create",
			"syllabi:createAsMentor",
			"image:create",
			"aTiP:visit",
			"aKits-authoring:visit",
			"problems:visit",
			"problems:owned",
			"problemSet:create",
			"problemSet:visit",
			"problem:create",
			"problem:view",
			"gradebooksRules:create",
			"gradebooksRules:visit",
			"gradebooks:visit",
			"gradeScale:visit"
		],
		staticPrivate: ["mentorDashboard:visit"],
		dynamic: {
			"gradebooksRules:manage": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"gradebooks:read": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"sipiTemplates:manage": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data) && (!data.template_status || data.template_status === ELEMENT_STATUS.DRAFT || data.template_status === ELEMENT_STATUS.READY);
			},
			"sipiTemplates:statusChange": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"sipiTemplates:mentor": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"sipiTemplatesComment:delete": isUserCommentOwner,
			"sipiTemplatesComment:edit": isUserCommentOwner,
			"syllabi:manage": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data) || data.course_variant_id === void 0 && Boolean(data.teacher) && Boolean(data.userId) && data.teacher === data.userId;
			},
			"syllabi:export": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"syllabi:statusChange": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data) || data.course_variant_id === void 0 && Boolean(data.teacher) && Boolean(data.userId) && data.teacher === data.userId;
			},
			"syllabi:mentor": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data) || data.course_variant_id === void 0 && Boolean(data.teacher) && Boolean(data.userId) && data.teacher === data.userId;
			},
			"ipCalendar:manage": function(data, role, roleModel) {
				return isActiveIPlanner(data) && model.accessValidator(roleModel, role, data);
			},
			"ipCalendar:edit": function(data, role, roleModel) {
				return isActiveIPlanner(data) && model.accessValidator(roleModel, role, data);
			},
			"tipCalendar:read": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"syllabiComment:delete": isUserCommentOwner,
			"syllabiComment:edit": isUserCommentOwner,
			"aTiP:view": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"problem:update": isUserAuthor,
			"problem:delete": isUserAuthor,
			"problemSet:manage": isUserAuthor,
			"quizSiPi:manage": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"problemSet:view": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data) && isTeacherTest(data) || isUserAuthor(data) || Boolean(data.from_sipi);
			},
			"resource:edit": function(data) {
				return isUserResourceOwner(data) && Boolean(data.is_valid);
			},
			"resource:editContent": function(data) {
				return isUserResourceOwner(data) && !data.used_by_another_user && Boolean(data.is_valid);
			},
			"resource:clone": is_cloneable,
			"resource:delete": function(data) {
				return isUserResourceOwner(data) && !data.is_used;
			},
			"ipStats:read": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			}
		}
	});
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/superMentor.ts
function createSuperMentor() {
	const model = inheritRole(createMentor(), {
		name: "Super Mentor",
		plural: "Super Mentors",
		roleName: ROLE.SUPER_MENTOR,
		entityType: "COURSE",
		assignedEntityId: "course_variant_id",
		staticPrivate: ["superMentorDashboard:visit"],
		dynamic: {
			"course:manage": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"sipiTemplates:superMentor": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"sipiTemplates:manage": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"sipiTemplates:statusChange": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			}
		}
	});
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/blockAdmin.ts
function createBlockAdmin() {
	const model = inheritRole(createSuperMentor(), {
		name: "Block Admin",
		plural: "Block Admins",
		roleName: ROLE.BLOCK_ADMIN,
		entityType: "BLOCK",
		assignedEntityId: "block_id",
		useToLimitDataFind: true,
		static: [
			"block:visit",
			"compTest:visit",
			"compTest:view",
			"compQuestion:visit",
			"compQuestion:view",
			"compQuestion:create",
			"compProposal:load",
			"compProposal:visit",
			"compTest:settings"
		],
		staticPrivate: ["blockAdminDashboard:visit"],
		dynamic: {
			"course:grant": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data) && data.grantRole != void 0 && ROLES_COURSE.includes(data.grantRole);
			},
			"compQuestion:view": function(data, role, roleModel) {
				return model.accessValidatorWithFreeForAll(roleModel, role, data) && model.accessValidatorIntersectionOrEmpty(data.myBlockIDs, data.block_id);
			},
			"compQuestion:manage": function(data, role, roleModel) {
				return model.accessValidatorWithFreeForAll(roleModel, role, data) && questionIsEditable(data?.testStatuses) && model.accessValidatorIntersectionOrEmpty(data.myBlockIDs, data.block_id);
			},
			"compQuestion:remove": function(data, role, roleModel) {
				return model.accessValidatorWithFreeForAll(roleModel, role, data) && questionIsEditable(data?.testStatuses) && model.accessValidatorIntersectionOrEmpty(data.myBlockIDs, data.block_id);
			},
			"compQuestion:review": function(data, role, roleModel) {
				return model.accessValidatorWithFreeForAll(roleModel, role, data) && questionIsEditable(data?.testStatuses) && model.accessValidatorIntersectionOrEmpty(data.myBlockIDs, data.block_id);
			},
			"compTest:grant": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data) && ROLE.QAD === data.grantRole;
			},
			"compProposal:manage": function(data) {
				return model.accessValidatorIntersectionOrEmpty(data.myBlockIDs, data.block_id) && data.newStatus !== "Draft" && data.status !== "Draft";
			}
		}
	});
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/schoolAssessmentManager.ts
function createSchoolAssessmentManager() {
	const model = inheritRole(new BaseRole(), {
		name: "School Assessment Manager",
		plural: "School Assessment Managers",
		roleName: ROLE.SCHOOL_ASSESSMENT_MANAGER,
		entityType: "SCHOOL",
		assignedEntityId: "school_id",
		useToLimitDataFind: true,
		static: [
			"compTest:school:overview:visit",
			"nativeTest:settings",
			"nativeTest:visit",
			"nativeTest:view"
		],
		dynamic: {
			"compTest:school:load": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"nativeTest:fill": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"problem:update": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			}
		}
	});
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/testWriter.ts
function createTestWriter() {
	const model = inheritRole(new BaseRole(), {
		name: "Test Writer",
		plural: "Test Writers",
		roleName: ROLE.TEST_WRITER,
		entityType: "COURSE",
		assignedEntityId: "course_variant_id",
		useToLimitDataFind: true,
		static: [
			"syllabi:visit",
			"sipiTemplates:visit",
			"schoolCalendar:read",
			"ipCalendar:read",
			"course:read",
			"course:visit",
			"block:read",
			"image:create",
			"aTiP:visit",
			"aKits-authoring:visit",
			"nativeTest:visit",
			"nativeTest:view",
			"nativeTest:create",
			"nativeTest:settings",
			"compQuestion:createInTest",
			"compQuestion:filterByUsage",
			"problems:visit",
			"problems:owned",
			"problem:create",
			"problem:view",
			"compQuestion:visit",
			"compTest:visit"
		],
		dynamic: {
			"aTiP:view": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"compQuestion:view": function(data) {
				return model.accessValidatorIntersectionOrEmpty(data.myBlockIDs, data.block_id);
			},
			"compQuestion:manage": function(data, role, roleModel) {
				return isUserAuthor(data) && model.accessValidatorWithFreeForAll(roleModel, role, data) && (!data.comp_test_id || data.comp_test_id.length <= 1) && questionIsEditable(data?.testStatuses) && model.accessValidatorIntersectionOrEmpty(data.myBlockIDs, data.block_id);
			},
			"compQuestion:remove": function(data, role, roleModel) {
				return isUserAuthor(data) && model.accessValidatorWithFreeForAll(roleModel, role, data) && (!data.comp_test_id || data.comp_test_id.length <= 1) && questionIsEditable(data?.testStatuses) && model.accessValidatorIntersectionOrEmpty(data.myBlockIDs, data.block_id);
			},
			"compTest:view": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"compTest:manage": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"nativeTest:manage": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"nativeTest:fill": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"nativeTest:delete": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"problem:update": isUserAuthor,
			"problem:delete": isUserAuthor
		}
	});
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/assessmentAdmin.ts
function createAssessmentAdmin() {
	const tWriter = createTestWriter();
	const compQEditor = createCompQuestionsEditor();
	const sam = createSchoolAssessmentManager();
	const model = inheritRole(new BaseRole(), {
		name: "Assessment Admin",
		plural: "Assessment Admins",
		roleName: ROLE.ASSESSMENT_ADMIN,
		entityType: "TENANT",
		assignedEntityId: "tenant_id",
		useToLimitDataFind: true
	});
	model.static = [...new Set([
		...tWriter.static,
		...compQEditor.static,
		...sam.static,
		"schoolCourse:visit",
		"compTest:visit",
		"compTest:view",
		"compTest:create",
		"compTest:settings",
		"compTest:settings:default",
		"nativeTest:settings",
		"nativeTest:settings:default",
		"compTest:school:load:all",
		"school:visit",
		"courseFollowers:visit",
		"compQuestion:create",
		"compQuestion:release",
		"problemSet:create",
		"problemSet:visit",
		"compQuestion:invalidate"
	])];
	model.dynamic = {
		...tWriter.dynamic,
		...compQEditor.dynamic,
		...sam.dynamic,
		"course:manage": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"course:grant": function(data, role, roleModel) {
			const { grantRole } = data;
			return model.accessValidator(roleModel, role, data) && grantRole === ROLE.TEST_WRITER;
		},
		"compQuestion:view": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"compQuestion:manage": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data) && questionIsEditable(data?.testStatuses);
		},
		"compQuestion:remove": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data) && questionIsEditable(data?.testStatuses);
		},
		"compQuestion:review": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data) && questionIsEditable(data?.testStatuses);
		},
		"compTest:delete": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"compTest:publish": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data) && Boolean(data.comp_test_id) && data.status === COMP_TEST_STATUS.LOCKED;
		},
		"compTest:grant": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data) && ROLE.QAD === data.grantRole;
		},
		"school:grant": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data) && ROLE.SCHOOL_ASSESSMENT_MANAGER === data.grantRole;
		},
		"compTest:school:access:manage": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"problemSet:manage": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"problemSet:view": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		}
	};
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/contentCoordinator.ts
function createContentCoordinator() {
	const aAdmin = createAssessmentAdmin();
	const bAdmin = createBlockAdmin();
	const pAdmin = createPackageAdmin();
	const model = inheritRole(new BaseRole(), {
		name: "Content Coordinator",
		plural: "Content Coordinators",
		roleName: ROLE.CONTENT_COORDINATOR,
		entityType: "TENANT",
		assignedEntityId: "tenant_id",
		useToLimitDataFind: true
	});
	model.static = [...new Set([
		...aAdmin.static,
		...bAdmin.static,
		...pAdmin.static,
		"users:visit",
		"syllabi:visit",
		"sipiTemplates:visit",
		"sipiTemplates:aTipCreate",
		"schoolCourse:visit",
		"sipiTemplatesComment:delete",
		"sipiTemplatesComment:edit",
		"sipiTemplatesComment:resolve",
		"syllabiComment:delete",
		"syllabiComment:edit",
		"syllabiComment:resolve",
		"clipart:create",
		"dndResource:manage",
		"resource:setLicense",
		"topicCollection:sync",
		"topicCollection:manage",
		"courseFollowers:visit",
		"aKit:manageTenantSettings",
		"combinedGrade:visit"
	])];
	model.dynamic = {
		...aAdmin.dynamic,
		...bAdmin.dynamic,
		...pAdmin.dynamic,
		"gradebooks:read": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"gradebooks:manage": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"combinedGrade:read": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"combinedGrade:manage": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"block:manage": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"syllabi:manage": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"ipAssignments:manage": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"syllabi:export": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"sipiTemplates:manage": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"block:grant": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data) && data.grantRole !== void 0 && ROLES_BLOCK.includes(data.grantRole);
		},
		"tenant:grant": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data) && data.grantRole !== void 0 && [ROLE.CONTENT_COORDINATOR, ROLE.USER].includes(data.grantRole);
		},
		"package:remove": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"aTiP:statusChange": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"aTiP:contentCoordinator": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"sipiTemplates:contentCoordinator": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"lessonPath:manage": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		}
	};
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/schoolRegistrar.ts
function createSchoolRegistrar() {
	const model = inheritRole(new BaseRole(), {
		name: "School Registrar",
		plural: "School Registrars",
		roleName: ROLE.SCHOOL_REGISTRAR,
		entityType: "SCHOOL",
		assignedEntityId: "school_id",
		useToLimitDataFind: true,
		static: ["attendance:visit", "student:visit"],
		dynamic: {
			"attendance:view": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"classes:view": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"attendance:manage": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"schoolCalendar:read": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"student:view": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"student:manage": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"distanceSupport:view": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"distanceSupport:manage": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			}
		}
	});
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/schoolAssignmentReviewer.ts
function createSchoolAssignmentReviewer() {
	const model = inheritRole(new BaseRole(), {
		name: "School Assignment Reviewer",
		plural: "School Assignment Reviewers",
		roleName: ROLE.SCHOOL_ASSIGNMENT_REVIEWER,
		entityType: "SCHOOL",
		assignedEntityId: "school_id",
		useToLimitDataFind: true,
		static: ["syllabi:visit", "course:read"],
		dynamic: {
			"ipAssignments:read": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"ipTabletView:read": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			}
		}
	});
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/schoolAdmin.ts
function createSchoolAdmin() {
	const registrar = createSchoolRegistrar();
	const assignmentReviewer = createSchoolAssignmentReviewer();
	const model = inheritRole(new BaseRole(), {
		name: "School Admin",
		plural: "School Admins",
		roleName: ROLE.SCHOOL_ADMIN,
		entityType: "SCHOOL",
		assignedEntityId: "school_id",
		useToLimitDataFind: true
	});
	model.static = [...new Set([
		...registrar.static,
		...assignmentReviewer.static,
		"course:visit",
		"course:read",
		"schoolCourse:visit",
		"schoolCourse:read",
		"classes:visit",
		"userCourses:visit",
		"school:visit",
		"users:visit",
		"block:read",
		"schoolAdminDashboard:visit",
		"gradebooks:visit",
		"combinedGrade:visit",
		"combinedGrade:manage",
		"gradeScale:visit",
		"gradeScale:read"
	])];
	model.dynamic = {
		...registrar.dynamic,
		...assignmentReviewer.dynamic,
		"gradebooks:read": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"gradebooks:manage": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"classes:read": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"classes:manage": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"classes:editProperties": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"syllabi:export": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"syllabi:inactivate": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"syllabi:readUnaccepted": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"syllabi:changeTeacher": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"resource:setOwner": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"instructionalMaterial:export": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"school:manage": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"school:grant": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data) && data.grantRole !== void 0 && [ROLE.SCHOOL_REGISTRAR, ROLE.SCHOOL_ASSIGNMENT_REVIEWER].includes(data.grantRole);
		},
		"policy:changeStatus": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"featureSchool:manage": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"school:select": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"schoolCalendar:manage": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"schoolCalendar:visit": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"schoolCalendar:read": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"ipCalendar:read": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"ipCalendar:changeIPlanner": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"schoolAdminDashboard:view": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		/**
		* Checks the impersonated user doesn't have any tenant role except the teacher role and the impersonated
		* user doesn't have school role on school where, the school admin has not rights
		*/
		"users:impersonate": function(data) {
			if (data.hasAtMostTheTeacherTenantRole !== true) return false;
			if (data.memberOfSchools) if (data.memberOfSchools.length > 0) return data.memberOfSchools.reduce((isGranted, school_id) => {
				if (!isMemberOfSchool({
					school_id,
					memberOfSchools: data.schoolAdminOfSchools || []
				})) return false;
				return isGranted;
			}, true);
			else return false;
			return true;
		},
		"schoolCourseMeta:create": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"ipStats:read": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"ipStatsStudents:read": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		},
		"data:manage": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		}
	};
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/tenantCoordinator.ts
function createTenantCoordinator() {
	const schoolAdmin = createSchoolAdmin();
	const contentCoordinator = createContentCoordinator();
	const model = inheritRole(contentCoordinator, {
		name: "Tenant Coordinator",
		plural: "Tenant Coordinators",
		roleName: ROLE.TENANT_COORDINATOR,
		entityType: "TENANT",
		assignedEntityId: "tenant_id"
	});
	model.dynamic = {
		...schoolAdmin.dynamic,
		...contentCoordinator.dynamic,
		"tenant:grant": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data) && data.grantRole !== void 0 && ROLES_TENANT.includes(data.grantRole) && data.grantRole !== ROLE.ADMIN;
		},
		"users:impersonate": () => false
	};
	model.static = [...new Set([
		...schoolAdmin.static,
		...contentCoordinator.static,
		"school:visit",
		"school:grant",
		"year:visit",
		"year:manage",
		"data:manage",
		"schools:manage",
		"gradeScale:manage",
		"gradeReport:visit",
		"gradeReport:manage"
	])];
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/tabletAdmin.ts
function createTabletAdmin() {
	const model = inheritRole(new BaseRole(), {
		name: "Tablet Administrator",
		plural: "Tablet Administrators",
		roleName: ROLE.TABLET_ADMIN,
		entityType: "TENANT",
		assignedEntityId: "tenant_id",
		useToLimitDataFind: false,
		static: [
			"year:visit",
			"tablet:visit",
			"tablet:manage",
			"publishedContent:view",
			"schoolCourse:visit",
			"school:visit",
			"course:read",
			"classes:visit",
			"userCourses:visit",
			"student:visit"
		],
		dynamic: {
			"school:grant": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data) && ROLE.SCHOOL_TABLET_ADMIN === data.grantRole;
			},
			"student:view": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"school:select": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"classes:read": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			}
		}
	});
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/schoolTabletAdmin.ts
function createSchoolTabletAdmin() {
	const model = inheritRole(new BaseRole(), {
		name: "School Tablet Administrator",
		plural: "School Tablet Administrators",
		roleName: ROLE.SCHOOL_TABLET_ADMIN,
		entityType: "SCHOOL",
		assignedEntityId: "school_id",
		useToLimitDataFind: true,
		static: [
			"tablet:visit",
			"course:read",
			"classes:visit",
			"userCourses:visit",
			"student:visit"
		],
		dynamic: {
			"tablet:manage": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"student:view": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"school:select": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"classes:read": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			}
		}
	});
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/admin.ts
function createAdmin() {
	const model = inheritRole(createTenantCoordinator(), {
		name: "Admin",
		plural: "Admins",
		roleName: ROLE.ADMIN,
		entityType: "TENANT",
		assignedEntityId: "tenant_id",
		dynamic: {
			"resource:edit": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"resource:editContent": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"resource:setOwner": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"resource:setValidity": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"resource:delete": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			}
		},
		static: [
			"year:visit",
			"year:manage",
			"data:manage",
			"schools:manage",
			"tablet:visit",
			"tablet:manage",
			"tenant:grant",
			"course:manage",
			"block:manage",
			"sync:manage",
			"sipiTemplatesComment:delete",
			"sipiTemplatesComment:edit",
			"sipiTemplatesComment:resolve",
			"aTiPComment:delete",
			"aTiPComment:edit",
			"aTiPComment:resolve",
			"syllabiComment:delete",
			"syllabiComment:edit",
			"syllabiComment:resolve",
			"glossary:manage",
			"kit:clone",
			"student:visit",
			"student:view",
			"student:manage",
			"school:select",
			"schoolCalendar:visit",
			"schoolCalendar:manage",
			"schoolCalendar:read",
			"distanceSupport:view",
			"distanceSupport:manage"
		]
	});
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/impersonator.ts
var LEGENDS_TENANT_ID = 6;
function createImpersonator() {
	const model = inheritRole(new BaseRole(), {
		name: "Impersonator",
		plural: "Impersonators",
		roleName: ROLE.IMPERSONATOR,
		entityType: "TENANT",
		assignedEntityId: "tenant_id",
		useToLimitDataFind: false,
		static: ["users:visit"],
		dynamic: { "users:impersonate": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data);
		} },
		isHiddenForAssignmentFn: (data) => data?.tenant_id === LEGENDS_TENANT_ID
	});
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/teacher.ts
var isNotComprehensiveTest = (data) => {
	const type = data && data.psetType && data.psetType.toLowerCase();
	return !(type && (type === "pc" || type === "c"));
};
var isTeacher = ({ userId, teacher }) => {
	return isUserTeacher({
		teacherId: teacher,
		userId
	});
};
var isTeacherOrCoTeacher = ({ userId, teacher, sectionTeachers }) => {
	const dataForTeachers = {
		teachers: sectionTeachers,
		userId
	};
	return isTeacher({
		userId,
		teacher
	}) || isUserOneOfTeachers(dataForTeachers);
};
var isTeacherOrCoTeacherOrSubstitute = ({ teacher, userId, sectionTeachers, sectionSubstitutes, iPlannerSectionsTeachers }) => {
	const teachers = sectionTeachers && Array.isArray(sectionTeachers) && sectionTeachers.length > 0 ? sectionTeachers : iPlannerSectionsTeachers;
	const dataForSubstitutes = {
		substitutes: sectionSubstitutes || [],
		userId
	};
	return isTeacherOrCoTeacher({
		userId,
		teacher,
		sectionTeachers: teachers
	}) || isUserOneOfSubstitutes(dataForSubstitutes);
};
function createTeacher() {
	const model = inheritRole(new BaseRole(), {
		name: "Teacher",
		plural: "Teachers",
		roleName: ROLE.USER,
		entityType: "TENANT",
		assignedEntityId: "tenant_id",
		useToLimitDataFind: false,
		static: [
			"syllabi:visit",
			"sipiTemplates:visit",
			"myMessages:visit",
			"syllabi:create",
			"syllabiComment:resolve",
			"problems:visit",
			"problems:owned",
			"problemSet:create",
			"problemSet:visit",
			"nativeTest:visit",
			"nativeTest:view",
			"course:read",
			"block:read",
			"problem:create",
			"problem:view",
			"image:create",
			"compQuestion:propose",
			"compProposal:visit",
			"compProposal:create",
			"compProposal:load",
			"nativeTest:settings",
			"aKits-instruction:visit",
			"gradebooks:visit",
			"combinedGrade:visit",
			"gradeScale:visit",
			"gradeScale:read"
		],
		staticPrivate: ["teacherDashboard:visit"],
		dynamic: {
			"classes:editProperties": isUserOneOfTeachers,
			"syllabi:manage": ({ teacher, userId, ccas, syllabus_status, iPlannerSectionsTeachers, createdBy, sectionSubstitutes }) => {
				if (!teacher && !ccas || !userId) return false;
				return (teacher === userId || ccas !== void 0 && !!ccas[userId]) && (!syllabus_status || syllabus_status === ELEMENT_STATUS.DRAFT || syllabus_status === ELEMENT_STATUS.READY) || isTeacherOrCoTeacher({
					userId,
					sectionTeachers: iPlannerSectionsTeachers
				}) || userId === createdBy && isTeacherOrCoTeacherOrSubstitute({
					userId,
					sectionSubstitutes: sectionSubstitutes || []
				});
			},
			"sipi:create": isTeacherOrCoTeacherOrSubstitute,
			"gradebooks:read": function(data) {
				return isTeacherOrCoTeacher(data);
			},
			"gradebooks:manage": function(data) {
				return isTeacherOrCoTeacher(data);
			},
			"syllabi:teacher": ({ teacher, userId, ccas }) => {
				if (!teacher && !ccas || !userId) return false;
				return teacher === userId || ccas !== void 0 && !!ccas[userId];
			},
			"syllabi:statusChange": ({ teacher, userId, ccas }) => {
				if (!teacher && !ccas || !userId) return false;
				return teacher === userId || ccas !== void 0 && !!ccas[userId];
			},
			"syllabi:export": ({ teacher, userId, ccas }) => {
				if (!teacher && !ccas || !userId) return false;
				return teacher === userId || ccas !== void 0 && !!ccas[userId];
			},
			"ipCalendar:manage": function(data) {
				return isActiveIPlanner(data) && isTeacher(data);
			},
			"ipCalendar:read": isTeacherOrCoTeacherOrSubstitute,
			"ipCalendar:edit": function(data) {
				return isActiveIPlanner(data) && isTeacherOrCoTeacherOrSubstitute(data);
			},
			"ipAssignments:read": isTeacherOrCoTeacherOrSubstitute,
			"ipAssignments:manage": isTeacherOrCoTeacherOrSubstitute,
			"sipiTemplates:manage": ({ ccas, userId, template_status }) => {
				if (!ccas || !userId) return false;
				return !!ccas[userId] && (!template_status || template_status === ELEMENT_STATUS.DRAFT || template_status === ELEMENT_STATUS.READY);
			},
			"sipiTemplatesComment:delete": isUserCommentOwner,
			"sipiTemplatesComment:edit": isUserCommentOwner,
			"syllabiComment:delete": isUserCommentOwner,
			"syllabiComment:edit": isUserCommentOwner,
			"kitComment:delete": isUserCommentOwner,
			"assets:create": isTeacherOrCoTeacherOrSubstitute,
			"kitComment:edit": isUserCommentOwner,
			"resource:edit": function(data) {
				return isUserResourceOwner(data) && Boolean(data.is_valid);
			},
			"resource:editContent": function(data) {
				return isUserResourceOwner(data) && !data.used_by_another_user && Boolean(data.is_valid);
			},
			"resource:clone": is_cloneable,
			"resource:delete": function(data) {
				return isUserResourceOwner(data) && !data.is_used;
			},
			"problemSet:manage": isUserAuthor,
			"problemSet:view": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data) && isNotComprehensiveTest(data) || isUserAuthor(data) || Boolean(data.from_sipi);
			},
			"nativeTest:fill": isUserTeacher,
			"quizSiPi:manage": function(data) {
				return isUserTeacher(data) || isTeacherOrCoTeacher(data) || data.createdBy === data.userId && isTeacherOrCoTeacherOrSubstitute(data);
			},
			"quizSiPi:create": isTeacherOrCoTeacherOrSubstitute,
			"problem:update": isUserAuthor,
			"problem:delete": isUserAuthor,
			"compQuestion:manage": function(data) {
				return isUserAuthor(data) && data.isActiveProposal === true;
			},
			"compQuestion:remove": function(data) {
				return isUserAuthor(data) && (data.clonedToProblem === true || data.isActiveProposal === true);
			},
			"compProposal:edit": function(data) {
				return isUserAuthor(data) && data.newStatus !== "Accepted" && data.newStatus !== "Rejected" && data.status !== "Accepted" && data.status !== "Closed";
			},
			"schoolCalendar:read": isMemberOfSchool,
			"ipStats:read": isTeacherOrCoTeacherOrSubstitute,
			"ipStatsStudents:read": isTeacherOrCoTeacherOrSubstitute
		}
	});
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/tenantRegistrar.ts
function createTenantRegistrar() {
	const model = inheritRole(createSchoolRegistrar(), {
		name: "Tenant Registrar",
		plural: "Tenant Registrars",
		roleName: ROLE.TENANT_REGISTRAR,
		entityType: "TENANT",
		assignedEntityId: "tenant_id",
		static: ["school:visit"],
		dynamic: { "school:grant": function(data, role, roleModel) {
			return model.accessValidator(roleModel, role, data) && ROLE.SCHOOL_REGISTRAR === data.grantRole;
		} }
	});
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/sporkCoordinator.ts
function createSporkCoordinator() {
	return inheritRole(new BaseRole(), {
		name: "SPORK Coordinator",
		plural: "SPORK Coordinators",
		roleName: ROLE.SPORK_COORDINATOR,
		entityType: ROLE_ENTITY_TYPE.APPLICATION,
		assignedEntityId: void 0,
		isHiddenForAssignmentFn: () => true,
		static: [
			"tenant:manage",
			"tenant:grant",
			"tenant:feature",
			"survey:manage",
			"microlearning:manage",
			"aTiP:publish"
		]
	});
}
//#endregion
//#region ../../spork-common/src/auth/roles/resourceAdmin.ts
function createResourceAdmin() {
	const model = inheritRole(new BaseRole(), {
		name: "Resource Admin",
		plural: "Resource Admins",
		roleName: ROLE.RESOURCE_ADMIN,
		entityType: "TENANT",
		assignedEntityId: "tenant_id",
		static: [
			"syllabi:visit",
			"sipiTemplates:visit",
			"resource:setLicense",
			"course:read",
			"block:read"
		],
		dynamic: {
			"resource:edit": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"resource:editContent": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"resource:setOwner": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"resource:setValidity": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"resource:delete": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			}
		}
	});
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/commentRecipient.ts
function createCommentRecipient() {
	return inheritRole(new BaseRole(), {
		name: "Comment Recipient",
		plural: "Comment Recipients",
		roleName: ROLE.COMMENT_RECIPIENT,
		entityType: "KIT",
		assignedEntityId: "kit_id"
	});
}
//#endregion
//#region ../../spork-common/src/auth/roles/surveyAdmin.ts
function createSurveyAdmin() {
	return inheritRole(new BaseRole(), {
		name: "Survey Admin",
		plural: "Survey Admins",
		roleName: ROLE.SURVEY_ADMIN,
		entityType: ROLE_ENTITY_TYPE.TENANT,
		assignedEntityId: "tenant_id",
		isHiddenForAssignmentFn: () => true,
		static: ["survey:manage"]
	});
}
//#endregion
//#region ../../spork-common/src/auth/roles/microlearningAdmin.ts
function createMicrolearningAdmin() {
	return inheritRole(new BaseRole(), {
		name: "Microlearning Admin",
		plural: "Microlearning Admins",
		roleName: ROLE.MICROLEARNING_ADMIN,
		entityType: ROLE_ENTITY_TYPE.TENANT,
		assignedEntityId: "tenant_id",
		isHiddenForAssignmentFn: () => true,
		static: ["microlearning:manage"]
	});
}
//#endregion
//#region ../../spork-common/src/auth/roles/packageProblemEditor.ts
function createPackageProblemEditor() {
	const model = inheritRole(new BaseRole(), {
		name: "Package Problem Editor",
		plural: "Package Problem Editors",
		roleName: ROLE.PACKAGE_PROBLEM_EDITOR,
		entityType: ROLE_ENTITY_TYPE.PACKAGE,
		assignedEntityId: "package_id",
		useToLimitDataFind: true,
		isHiddenFromRoleList: true,
		static: [],
		dynamic: {
			"problem:update": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"problemSet:manage": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"problemSet:view": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			},
			"quizSiPi:manage": function(data, role, roleModel) {
				return model.accessValidator(roleModel, role, data);
			}
		}
	});
	return model;
}
//#endregion
//#region ../../spork-common/src/auth/roles/gradeScaleAdmin.ts
function createGradeScaleAdmin() {
	return inheritRole(new BaseRole(), {
		name: "Grade Scale Admin",
		plural: "Grade Scale Admins",
		roleName: ROLE.GRADE_SCALE_ADMIN,
		entityType: "TENANT",
		assignedEntityId: "tenant_id",
		static: [
			"gradeScale:manage",
			"gradeReport:visit",
			"gradeReport:manage"
		],
		dynamic: {}
	});
}
//#endregion
//#region ../../spork-common/src/auth/rbac-rules.ts
/**
* Example:
*     ROLE_SUPERAUTHOR: {
*         name: "Super Author",         // display name of role
*         plural: "Super Authors",      // display name in case of count > 1 (e.g. on tenant card)
*         roleName: ROLE.SUPERAUTHOR,   // the name of the roles as stored in database
*         assignedEntity: "kit",        // entity object in database
*         assignedEntityId: "id",       // entity object's property which contains the ID
*         static: ["posts:list", "posts:create", "users:getSelf", "home-page:visit", "dashboard-page:visit"],
*         dynamic: {
*            "posts:edit": ({ userId, postOwnerId }) => {
*                if (!userId || !postOwnerId) return false;
*                return userId === postOwnerId;
*             },
*         },
*     },
*
*/
var roleModels = {
	[ROLE.ADMIN]: createAdmin(),
	[ROLE.SPORK_COORDINATOR]: createSporkCoordinator(),
	[ROLE.SUPERAUTHOR]: createSuperAuthor(),
	[ROLE.PACKAGE_ADMIN]: createPackageAdmin(),
	[ROLE.CONTENT_COORDINATOR]: createContentCoordinator(),
	[ROLE.ASSESSMENT_ADMIN]: createAssessmentAdmin(),
	[ROLE.TENANT_COORDINATOR]: createTenantCoordinator(),
	[ROLE.SCHOOL_ADMIN]: createSchoolAdmin(),
	[ROLE.SCHOOL_ASSIGNMENT_REVIEWER]: createSchoolAssignmentReviewer(),
	[ROLE.BLOCK_ADMIN]: createBlockAdmin(),
	[ROLE.SUPER_MENTOR]: createSuperMentor(),
	[ROLE.MENTOR]: createMentor(),
	[ROLE.TEST_WRITER]: createTestWriter(),
	[ROLE.TABLET_ADMIN]: createTabletAdmin(),
	[ROLE.SCHOOL_TABLET_ADMIN]: createSchoolTabletAdmin(),
	[ROLE.QAD]: createQad(),
	[ROLE.COMP_QUESTIONS_EDITOR]: createCompQuestionsEditor(),
	[ROLE.RESOURCE_ADMIN]: createResourceAdmin(),
	[ROLE.AUTHOR]: createAuthor(),
	[ROLE.SCHOOL_ASSESSMENT_MANAGER]: createSchoolAssessmentManager(),
	[ROLE.SCHOOL_REGISTRAR]: createSchoolRegistrar(),
	[ROLE.TENANT_REGISTRAR]: createTenantRegistrar(),
	[ROLE.REVIEWER]: createReviewer(),
	[ROLE.USER]: createTeacher(),
	[ROLE.COMMENT_RECIPIENT]: createCommentRecipient(),
	[ROLE.PACKAGE_PROBLEM_EDITOR]: createPackageProblemEditor(),
	[ROLE.AUTHORING_MENTOR]: createAuthoringMentor(),
	[ROLE.AUTHORING_SUPER_MENTOR]: createAuthoringSuperMentor(),
	[ROLE.IMPERSONATOR]: createImpersonator(),
	[ROLE.SURVEY_ADMIN]: createSurveyAdmin(),
	[ROLE.MICROLEARNING_ADMIN]: createMicrolearningAdmin(),
	[ROLE.GRADE_SCALE_ADMIN]: createGradeScaleAdmin()
};
//#endregion
//#region ../../spork-common/src/auth/isPermitted.ts
/**
* Check if single role has permission for action.
*
* @param {string} role user role (RBAC roles key)
* @param {string} action which action permission to check
* @param {object} data security context (like { userId, authorId })
*/
var check = (role, action, data) => {
	const permissions = roleModels[role.role];
	if (!permissions) return false;
	if ([...permissions.static || [], ...permissions.staticPrivate || []].includes(action)) return true;
	if (permissions.dynamic || permissions.dynamicPrivate) {
		const dynamicPermissions = {
			...permissions.dynamic || {},
			...permissions.dynamicPrivate || {}
		};
		return !!(typeof dynamicPermissions[action] === "function" && dynamicPermissions[action](data || {}, role, permissions));
	}
	return false;
};
/**
* Check is any of user's roles have permission to do action
* @param {string[]} roles user roles in list of { role, entity_id, entity_type } objects from SQL DB
* @param {string} action which action permission to check
* @param {object} data security context (like { userId, authorId })
*/
var checkRoles = (roles, action, data) => roles.findIndex((role) => {
	if (data && Array.isArray(data.grantRoles)) return data.grantRoles.some((grantRole) => check(role, action, {
		...data,
		grantRole
	}));
	else return check(role, action, data);
}) !== -1;
/**
* If possible, use hasPermission from "@spork/client-common" instead of this.
*/
var isPermitted = ({ userRoles, action, actionArray, data }) => {
	if (action && typeof action === "string") return checkRoles(userRoles, action, data);
	const actions = action || actionArray;
	if (Array.isArray(actions) && actions.length) return actions.findIndex((a) => checkRoles(userRoles, a, data)) !== -1;
	return false;
};
//#endregion
//#region ../../spork-common/src/utils/compareUtils.ts
/** Compare function to be used within Array.sort() to sort data case insensitively by "name" property */
var compareByNameCI = (a, b) => {
	const av = (a && a.name || "").toLocaleLowerCase();
	const bv = (b && b.name || "").toLocaleLowerCase();
	return av === bv ? 0 : av < bv ? -1 : 1;
};
//#endregion
//#region ../../spork-common/src/types.ts
var PageFormat = /* @__PURE__ */ function(PageFormat) {
	PageFormat["US_LETTER"] = "Letter";
	PageFormat["A4"] = "A4";
	return PageFormat;
}({});
//#endregion
//#region ../../spork-common/src/api/Auth.ts
/**
* The URLs used for getting access token
*/
var LOGIN_URL = /* @__PURE__ */ function(LOGIN_URL) {
	LOGIN_URL["USERNAME"] = "login";
	LOGIN_URL["TOKEN"] = "token";
	LOGIN_URL["QR_CODE"] = "login/qrcode";
	LOGIN_URL["SSO"] = "sso";
	return LOGIN_URL;
}({});
//#endregion
//#region src/JwtAuthenticationClient.ts
var debug$3 = makeDebug("feathers-client:JwtAuthenticationClient");
var KEY_LOGOUT = "logoutTabs";
var KEY_JWT_CLAIMS = "jwtClaims";
var KEY_JWT_SSID = "jwt";
var KEY_JWT_SSID_TEMP = "jwtTemp";
var AUTH_EVENT = {
	LOGOUT: "logout",
	AUTHENTICATED: "authenticated",
	NO_VALID_TOKEN: "no_valid_token",
	REAUTHENTICATION_FAILED: "reAuthenticate_error"
};
function convertAxiosErrorToFeathersError(axiosError) {
	const feathersError = axiosError.response?.data;
	return new FeathersError(axiosError.message, feathersError?.name || axiosError.name, feathersError?.code || axiosError.status, feathersError?.className || "", feathersError?.data || axiosError.config);
}
/**
* JwtAuthenticationClient extends the AuthenticationClient class to provide JWT-based authentication functionality.
* This class handles authentication, token management, and related operations for a Feathers application.
*
* Key features:
* - Supports login with username/password or JWT
* - Handles token storage
* - Provides methods for impersonation and SSO token requests
* - Implements error handling for authentication-related issues
*/
var JwtAuthenticationClient = class extends AuthenticationClient {
	constructor(app, options) {
		super(app, options);
		this.isPersistentLogin = false;
		this.#jwtAccessToken = null;
		if (options.logout_from_all_tabs) addEventListener("storage", (event) => {
			if (this.authenticated && event.key === KEY_LOGOUT && event.newValue === null) {
				debug$3("Global logout event triggered. Logging out.");
				this.logout();
			}
		});
		if (global.localStorage?.getItem("disable_cookies")) this.options.use_cookies = false;
	}
	#jwtAccessToken;
	handleSocket(socket) {
		const connected = this.app.io ? "connect" : "open";
		const disconnected = this.app.io ? "disconnect" : "disconnection";
		socket.on(disconnected, () => {
			debug$3("Socket disconnected.");
			if (this.authenticated) {
				debug$3("Authenticated socket disconnected. Setting up re-authentication on future re-connect.");
				const authPromise = new Promise((resolve) => socket.once(connected, () => resolve())).then(() => this.reAuthenticate(true).catch((error) => {
					debug$3("Reauthentication failed with %s.", JSON.stringify(error));
					if (error.code >= 400 && error.code <= 403) this.app.emit(AUTH_EVENT.NO_VALID_TOKEN, error);
					else this.app.emit(AUTH_EVENT.REAUTHENTICATION_FAILED, error);
				}));
				this.app.set("authentication", authPromise);
			}
		});
	}
	/**
	* Make authenticated Axios REST API request.
	* @param axiosRequest
	* @returns
	*/
	async makeRestRequest(axiosRequest) {
		const accessToken = await this.getAccessToken();
		axiosRequest.headers = {
			...axiosRequest.headers,
			Authorization: `JWT ${accessToken}`
		};
		axiosRequest.baseURL = this.options.api_url;
		return axios(axiosRequest);
	}
	/**
	* Request an access token (JWT) from the server.
	* This method is used for all login scenarios, including username/password,
	* JWT token exchange, impersonation, and silent refresh.
	*
	* @param url - The login URL to send the request to
	* @param data - The data of the request (LoginWithUsername, LoginWithJWT, Impersonate, or SilentRefresh)
	* @param config - Optional Axios request configuration
	* @returns A promise that resolves to an AuthResponsePayload
	* @throws {FeathersError} If the request fails
	*/
	async requestAccessToken(url, data, config) {
		try {
			const requestConfig = { ...config };
			if (this.options.use_cookies) requestConfig.withCredentials = true;
			if (!requestConfig.headers?.Authorization) {
				const savedJwt = await this.storage.getItem(KEY_JWT_SSID);
				if (savedJwt) {
					debug$3("Using JWT from storage.");
					requestConfig.headers = { Authorization: `JWT ${savedJwt}` };
				}
			}
			const response = await axios.post(`${this.options.api_url}/${url}`, data, requestConfig);
			this.#jwtAccessToken = response.data.jwt;
			return response.data;
		} catch (err) {
			throw convertAxiosErrorToFeathersError(err);
		}
	}
	/**
	* Login with username and password. This method exchanges username and password for
	* access_token and refresh_token which are saved in sessionStorage/localStorage.
	*/
	async loginWithUsernamePassword(data) {
		debug$3("loginWithUsernamePassword: %s.", JSON.stringify(data));
		this.isPersistentLogin = data.is_persistent;
		if (!this.options.use_cookies) await this.storage.removeItem?.(KEY_JWT_SSID);
		const token = await this.requestAccessToken(LOGIN_URL.USERNAME, data);
		if (!this.options.use_cookies) await this.storage.setItem?.(KEY_JWT_SSID, token.jwt);
	}
	/**
	* Login with temporary time limited JWT token. This method exchanges temporary JWT token for
	* the real JWT token and also sets Cookies for next login.
	* This is used by Google and Microsoft login.
	*/
	async loginWithJwtToken(jwtToken, device_info, scheme = "JWT") {
		debug$3("loginWithJwtToken: %s.", JSON.stringify(jwtToken));
		this.isPersistentLogin = true;
		if (!this.options.use_cookies) await this.storage.removeItem?.(KEY_JWT_SSID);
		const token = await this.requestAccessToken(LOGIN_URL.TOKEN, {
			device_info,
			set_cookies: true,
			is_native_device: Boolean(this.options.is_native_device)
		}, { headers: { Authorization: `${scheme} ${jwtToken}` } });
		if (!this.options.use_cookies) await this.storage.setItem?.(KEY_JWT_SSID, token.jwt);
	}
	/**
	* Get a new JWT token based on provided impersonation data - impersonate user or switch to another
	* tenant.
	*/
	async impersonate(claims) {
		debug$3("impersonate: %s.", JSON.stringify(claims));
		await this.requestAccessToken(LOGIN_URL.TOKEN, claims || {});
		if (claims) this.storage.setItem?.(KEY_JWT_CLAIMS, JSON.stringify(claims));
		else this.storage.removeItem?.(KEY_JWT_CLAIMS);
		if (!this.options.use_cookies) if (claims) this.storage.setItem?.(KEY_JWT_SSID_TEMP, this.#jwtAccessToken);
		else this.storage.removeItem?.(KEY_JWT_SSID_TEMP);
	}
	/**
	* Get a new SSO token from server.
	*/
	async requestSSOToken() {
		return (await axios.post(`${this.options.api_url}/${LOGIN_URL.SSO}`, {}, { withCredentials: true })).data.jwt;
	}
	async loginWithQrCode(data) {
		debug$3("loginWithQrCode: %s.", JSON.stringify(data));
		return (await axios.post(`${this.options.api_url}/${LOGIN_URL.QR_CODE}`, data)).data;
	}
	/**
	* Get stored access token or request new token via REST API call.
	*/
	async getAccessToken() {
		if (this.#jwtAccessToken) return this.#jwtAccessToken;
		if (this.options.use_cookies) {
			debug$3("No access token found in memory. Trying to get one from HTTP Cookie.");
			const jwtClaims = await this.storage.getItem(KEY_JWT_CLAIMS);
			const requestData = jwtClaims ? {
				...JSON.parse(jwtClaims),
				silent_fail: true
			} : { silent_fail: true };
			await this.requestAccessToken(LOGIN_URL.TOKEN, requestData);
		} else {
			const token = await this.storage.getItem(KEY_JWT_SSID_TEMP) || await this.storage.getItem(KEY_JWT_SSID);
			this.#jwtAccessToken = token;
		}
		return this.#jwtAccessToken;
	}
	/**
	* Return the current access token from memory.
	*/
	getAccessTokenSync() {
		return this.#jwtAccessToken;
	}
	/**
	* @throws {FeathersError}
	*/
	async handleError(error) {
		debug$3("handleError: %s.", JSON.stringify(error));
		if (error.code >= 400 && error.code <= 403) await this.reset();
		return Promise.reject(error);
	}
	authenticate(authentication, params) {
		if (!authentication) return this.reAuthenticate();
		if (!this.app.io.connected) {
			debug$3("Socket is not opened yet. Opening.");
			this.app.io.open();
		}
		const promise = this.service.create(authentication, params).then((authResult) => {
			debug$3("Client authenticated the connection.");
			this.authenticated = true;
			this.app.emit(AUTH_EVENT.AUTHENTICATED, authResult);
			return authResult;
		}).catch((error) => this.handleError(error));
		this.app.set("authentication", promise);
		return promise;
	}
	async logout() {
		await this.reset();
		this.#jwtAccessToken = null;
		if (this.options.use_cookies) await axios.get(`${this.options.api_url}/logout`, { withCredentials: true });
		else this.storage.removeItem?.(KEY_JWT_SSID);
		this.storage.removeItem?.(KEY_JWT_CLAIMS);
		if (this.options.logout_from_all_tabs) {
			localStorage.setItem(KEY_LOGOUT, "true");
			localStorage.removeItem(KEY_LOGOUT);
		}
		this.app.emit(AUTH_EVENT.LOGOUT);
		return Promise.resolve(null);
	}
};
//#endregion
//#region src/client.ts
var debug$2 = makeDebug("feathers-client:client");
/**
* Create a socket.io connection to the backend server.
* Will NOT start polling the server.
* Websocket must be started by calling client.io.open() and stopped by client.io.close()
*
* @param nodeServerUrl URL of the backend server (ie. "http://localhost:3030")
* @param [forceOnlyWebsocket] if true, long polling fallback will be disabled
*/
var createConnection = (nodeServerUrl, forceOnlyWebsocket = false, socketOptions) => {
	const initSocket = io(nodeServerUrl, {
		transports: forceOnlyWebsocket ? ["websocket"] : ["websocket", "polling"],
		autoConnect: false,
		withCredentials: true,
		...socketOptions
	});
	if (!forceOnlyWebsocket) {
		initSocket.io.on("reconnect_attempt", (attempt) => {
			if (attempt > 4) initSocket.io.opts.transports = ["polling", "websocket"];
		});
		initSocket.io.on("reconnect", () => {
			initSocket.io.opts.transports = ["websocket"];
		});
	}
	return socketio(initSocket);
};
/**
* Setup resubscription to channels when re-connecting.
*/
var setupResubscription = (app) => {
	app.io.on("connect", () => {
		subscriptionMap.forEach((value) => {
			const { serviceName, options } = value;
			debug$2("Re-Subscribing for %s with options %o", serviceName, options);
			options.forEach((option) => app.service("subscribe").create({
				serviceName,
				where: option.subscribeFor
			}).catch((error) => {
				console.error(error);
			}));
		});
	});
};
/**
* Configure client with token storage.
* Should be used only in browser (which have sessionStorage).
*/
var configureAuthentication = (storage, options, typedClient) => {
	typedClient.configure(auth({
		storageKey: "access_token",
		jwtStrategy: "jwt",
		storage: new StorageWrapper(storage),
		Authentication: JwtAuthenticationClient,
		...options
	}));
};
/**
* Create a REST client that makes authenticated REST API calls.
* @returns a function that makes authenticated REST API calls
*/
var createRestClient = (typedClient) => (axiosRequest) => typedClient.authentication.makeRestRequest(axiosRequest);
//#endregion
//#region src/feathers-redux.ts
var queryResultDefaults = {
	total: 0,
	limit: 0,
	skip: 0,
	data: []
};
var initialState$1 = {
	isError: null,
	isLoading: false,
	isSaving: false,
	isFinished: false,
	data: null,
	queryResult: queryResultDefaults,
	searchIndex: {},
	createPending: false,
	findPending: false,
	getPending: false,
	updatePending: false,
	patchPending: false,
	removePending: false
};
/**
* Map function arguments to an object required for Redux createAction().
*/
function prepareDataForCreator(data, options) {
	return {
		payload: data,
		meta: options
	};
}
var getStorePath = (action) => action.meta?.arg?.m?.storeAs || "default";
var getStorePathForListenerAction = (action) => action.meta?.storeAs || "default";
var extractMetaFromServiceArg = (p) => ({
	p: {
		...p,
		meta: void 0
	},
	m: p?.meta
});
var getActionTypesForAsyncMethod = (method, actionType) => ({
	[`${method.toUpperCase()}_PENDING`]: `${actionType}/pending`,
	[`${method.toUpperCase()}_FULFILLED`]: `${actionType}/fulfilled`,
	[`${method.toUpperCase()}_REJECTED`]: `${actionType}/rejected`
});
/**
* Normalize query result to be stored in common format. Some API returns data as an array, some as paginated result.
* We should always store data as paginated and this function ensures that.
*
* @param data simple list of results or in paginated structure
*/
var normalizeQueryResult = (data) => {
	if (Array.isArray(data)) return {
		...queryResultDefaults,
		data: sanitizeData(data),
		total: data.length
	};
	else return data;
};
/**
* Generator of reducers for async thunk actions. Will add three reducers - for pending, fulfilled and rejected actions.
*
* @param builder
* @param action
* @param method
* @param isLoading
* @param isFind
*/
var createReducersForAsyncAction = (builder, action, method, isLoading = false, isFind = false) => {
	builder.addCase(action.pending, (state, action) => {
		const storePath = getStorePath(action);
		const silentRequest = action.meta?.arg?.m?.silent;
		return {
			...state,
			[storePath]: {
				...state[storePath] || initialState$1,
				isError: null,
				isLoading: isLoading && !silentRequest,
				isSaving: !isLoading,
				isFinished: false,
				[`${method}Pending`]: true
			}
		};
	}).addCase(action.fulfilled, (state, action) => {
		const storePath = getStorePath(action);
		const createSearchIndex = action.meta?.arg?.m?.saveToIndex;
		let newQueryResult = isFind ? normalizeQueryResult(action.payload) : state[storePath].queryResult;
		if (action.meta?.arg?.m?.doNotReplaceExisting) {
			const existingIds = state[storePath].queryResult.data.map((item) => item.id);
			const findResult = normalizeQueryResult(action.payload);
			const newItems = findResult.data.filter((item) => !existingIds.includes(item.id));
			const removedItemsIds = state[storePath].queryResult.data.filter((item) => !findResult.data.some((newItem) => newItem.id === item.id)).map((item) => item.id);
			const newQueryData = [...state[storePath].queryResult.data.filter((item) => !removedItemsIds.includes(item.id)), ...newItems];
			newQueryResult = {
				...newQueryResult,
				data: newQueryData,
				total: newQueryData.length
			};
		}
		return {
			...state,
			[storePath]: {
				...state[storePath],
				isError: null,
				isLoading: false,
				isSaving: false,
				isFinished: true,
				data: !isFind ? action.payload : null,
				queryResult: newQueryResult,
				[`${method}Pending`]: false,
				searchIndex: isFind && createSearchIndex ? {
					...state[storePath] ? state[storePath].searchIndex : {},
					...normalizeQueryResult(action.payload).data.reduce(function(result, item) {
						result[item.id] = item;
						return result;
					}, {})
				} : state[storePath] && state[storePath].searchIndex ? state[storePath].searchIndex : {}
			}
		};
	}).addCase(action.rejected, (state, action) => {
		const storePath = getStorePath(action);
		return {
			...state,
			[storePath]: {
				...state[storePath],
				isError: action.error,
				isLoading: false,
				isSaving: false,
				isFinished: true,
				queryResult: isFind ? { ...queryResultDefaults } : state[storePath].queryResult,
				[`${method}Pending`]: false
			}
		};
	});
};
/**
* remove all null values
*/
var sanitize = (data) => {
	Object.entries(data || {}).forEach((o) => o[1] === null ? delete data[o[0]] : 0);
};
/**
* Removes attributes with null values from given object or array of objects (data).
* This functionality is primarily used for updating redux store entry after being created/updated/patched on server side.
* Eventually it could be used in client for the same sanitization of data obtained by get/find if that data are used
* for updating/patching (for example aTip/Tip/iP SiPis).
* 
* Mutates original data.
*/
var sanitizeData = (data) => {
	if (Array.isArray(data)) data.forEach((item) => sanitize(item));
	else sanitize(data);
	return data;
};
/**
* Build a Redux compatible wrapper around a Feathers service.
*
* Instead of using a feathers-client service directly
*     app.services('messages').create({ name: 'John' }, (err, data) => {...});
* you first wrap the feathers service to expose Redux action creators and a reducer
*     messages = reduxifyService(app, 'messages');
* You can thereafter use the service in a standard Redux manner
*     store.dispatch(messages.create({ name: 'John' }));
* with async action creators being dispatched to a reducer which manages state.
*
* @param app the configured Feathers app, e.g. require('feathers-client')().configure(...)
* @param serviceName is the Feathers' route for the service.
*
* Should you wish to write additional action creators, the { reducer } export expects action types
*   '@Feathers/${SERVICE_NAME}/${METHOD}/pending', .../fulfilled and .../rejected
*
* The find service call stores Feathers' query payload in queryResult. Other methods store in data.
*
* isError is Feathers' error payload { message, name, code, className, errors }.
* If the feathers server response did not specify an error message, then the message property will
* be feathers default of 'Error'.
*
* Each service also gets a reset service call which re-initializes that service's state.
* This may be used, for example, to remove isError in order to no longer render error messages.
*/
var reduxifyService = (app, serviceName, extraReducers) => {
	const debug = makeDebug(`client-common:reducer:${serviceName}`);
	const SERVICE_NAME = `@Feathers/${serviceName}`;
	const service = app.service(serviceName);
	if (!service) {
		debug(`redux: Feathers service '${serviceName} does not exist.`);
		throw Error(`Feathers service '${serviceName} does not exist.`);
	}
	const FIND = `${SERVICE_NAME}/find`;
	const GET = `${SERVICE_NAME}/get`;
	const CREATE = `${SERVICE_NAME}/create`;
	const UPDATE = `${SERVICE_NAME}/update`;
	const PATCH = `${SERVICE_NAME}/patch`;
	const REMOVE = `${SERVICE_NAME}/remove`;
	const RESET = `${SERVICE_NAME}/reset`;
	const ON_CREATED = `${SERVICE_NAME}/on_created`;
	const ON_UPDATED = `${SERVICE_NAME}/on_updated`;
	const ON_PATCHED = `${SERVICE_NAME}/on_patched`;
	const ON_REMOVED = `${SERVICE_NAME}/on_removed`;
	/**
	* Create service actions to dispatch. Some of them are async (dispatch pending/fulfilled/rejected), some
	* of them are normal single actions.
	*/
	const serviceFindAction = createAsyncThunk(FIND, async ({ p, m }) => {
		const response = await service.find(p);
		if (m?.autoSubscribe) m.autoSubscribe(serviceName, normalizeQueryResult(response));
		return response;
	});
	const serviceGetAction = createAsyncThunk(GET, ({ id, p }) => service.get(id, p));
	const serviceCreateAction = createAsyncThunk(CREATE, ({ d, p }) => service.create(d, p));
	const serviceUpdateAction = createAsyncThunk(UPDATE, ({ id, d, p }) => service.update(id, d, p));
	const servicePatchAction = createAsyncThunk(PATCH, ({ id, d, p }) => service.patch(id, d, p));
	const serviceRemoveAction = createAsyncThunk(REMOVE, ({ id, p }) => service.remove(id, p));
	const onCreatedAction = createAction(ON_CREATED, prepareDataForCreator);
	const onUpdatedAction = createAction(ON_UPDATED, prepareDataForCreator);
	const onPatchedAction = createAction(ON_PATCHED, prepareDataForCreator);
	const onRemovedAction = createAction(ON_REMOVED, prepareDataForCreator);
	const onResetAction = createAction(RESET, (meta) => ({
		payload: [],
		...meta
	}));
	/**
	* Return result of reduxified service:
	*  - actions to dispatch
	*  - action types
	*  - reducer
	*/
	return {
		/**  Because createAsyncThunk() can have only one argument and our Feathers service calls
		* have usually 2-3 (id, data, params), it's necessary to wrap them into single object which
		* is then passed to actual async creator.
		*/
		find: (p) => serviceFindAction({ ...extractMetaFromServiceArg(p) }),
		get: (id, p) => serviceGetAction({
			id,
			...extractMetaFromServiceArg(p)
		}),
		create: (d, p) => serviceCreateAction({
			d,
			...extractMetaFromServiceArg(p)
		}),
		update: (id, d, p) => serviceUpdateAction({
			id,
			d,
			...extractMetaFromServiceArg(p)
		}),
		patch: (id, d, p) => servicePatchAction({
			id,
			d,
			...extractMetaFromServiceArg(p)
		}),
		remove: (id, p) => serviceRemoveAction({
			id,
			...extractMetaFromServiceArg(p)
		}),
		reset: onResetAction,
		onCreated: onCreatedAction,
		onUpdated: onUpdatedAction,
		onPatched: onPatchedAction,
		onRemoved: onRemovedAction,
		types: {
			...getActionTypesForAsyncMethod("find", FIND),
			...getActionTypesForAsyncMethod("get", GET),
			...getActionTypesForAsyncMethod("create", CREATE),
			...getActionTypesForAsyncMethod("update", UPDATE),
			...getActionTypesForAsyncMethod("patch", PATCH),
			...getActionTypesForAsyncMethod("remove", REMOVE),
			RESET,
			ON_CREATED,
			ON_UPDATED,
			ON_PATCHED,
			ON_REMOVED
		},
		reducer: createReducer({ default: initialState$1 }, (builder) => {
			createReducersForAsyncAction(builder, serviceFindAction, "find", true, true);
			createReducersForAsyncAction(builder, serviceGetAction, "get", true);
			createReducersForAsyncAction(builder, serviceCreateAction, "create");
			createReducersForAsyncAction(builder, serviceUpdateAction, "update");
			createReducersForAsyncAction(builder, servicePatchAction, "patch");
			createReducersForAsyncAction(builder, serviceRemoveAction, "remove");
			builder.addCase(onCreatedAction, (state, action) => {
				const storePath = getStorePathForListenerAction(action);
				if (!state[storePath]) return state;
				const createdData = action.payload;
				const currentQueryResult = state[storePath].queryResult || queryResultDefaults;
				let newData = currentQueryResult.data.concat(sanitizeData(createdData));
				const length = newData.length;
				newData = newData[0].id ? uniqBy(newData, "id") : uniqWith(newData, isEqual);
				if (length !== newData.length) debug(`redux:${ON_CREATED} duplicate records found`, action);
				const updatedResult = {
					...currentQueryResult,
					data: newData,
					total: newData.length
				};
				return {
					...state,
					[storePath]: {
						...state[storePath],
						queryResult: updatedResult
					}
				};
			}).addCase(onUpdatedAction, (state, action) => {
				const storePath = getStorePathForListenerAction(action);
				if (!state[storePath]) return state;
				return {
					...state,
					[storePath]: {
						...state[storePath],
						queryResult: {
							...state[storePath].queryResult,
							data: state[storePath].queryResult.data.map((item) => {
								if (item.id && item.id === action.payload.id) return sanitizeData(action.payload);
								return item;
							})
						}
					}
				};
			}).addCase(onPatchedAction, (state, action) => {
				const storePath = getStorePathForListenerAction(action);
				if (!state[storePath]) return state;
				const patchDataMap = Array.isArray(action.payload) ? action.payload.reduce((obj, item) => {
					obj[item.id] = item;
					return obj;
				}, {}) : { [action.payload.id]: action.payload };
				return {
					...state,
					[storePath]: {
						...state[storePath],
						queryResult: {
							...state[storePath].queryResult,
							data: state[storePath].queryResult.data.map((item) => {
								if (item.id && patchDataMap[item.id]) return sanitizeData({
									...item,
									...patchDataMap[item.id]
								});
								return item;
							})
						}
					}
				};
			}).addCase(onRemovedAction, (state, action) => {
				const storePath = getStorePathForListenerAction(action);
				if (!state[storePath]) return state;
				const currentData = state[storePath].queryResult.data;
				const currentSingleData = state[storePath].data;
				const removeDataIds = Array.isArray(action.payload) ? action.payload.map((item) => item.id) : [action.payload.id];
				const data = currentData.filter((item) => !(item.id && removeDataIds.includes(item.id)));
				const newSingleData = currentSingleData && !removeDataIds.includes(currentSingleData.id) ? currentSingleData : null;
				if (data.length === currentData.length && null === currentSingleData) {
					debug(`redux:${ON_REMOVED} records not found`, action);
					return state;
				}
				const howManyWeRemoved = currentData.length - data.length;
				const updatedResult = {
					...state[storePath].queryResult,
					data,
					total: state[storePath].queryResult.total - howManyWeRemoved
				};
				return {
					...state,
					[storePath]: {
						...state[storePath],
						data: newSingleData,
						queryResult: updatedResult
					}
				};
			}).addCase(onResetAction, (state, action) => {
				const storePath = getStorePathForListenerAction(action);
				if (state[storePath] && (state[storePath].isLoading || state[storePath].isSaving)) return state;
				return {
					...state,
					[storePath]: { ...initialState$1 }
				};
			});
			if (extraReducers) extraReducers(builder);
		})
	};
};
/**
* Method to bind a given dispatch function with the passed services.
*
* This helps with not having to pass down store.dispatch as a prop everywhere
* Read More: http://redux.js.org/docs/api/bindActionCreators.html
*
* @param {Object} services - using the default reduxifyService method
* @param {Function} dispatch - the relevant store.dispatch function which is to be bounded to actionCreators
* @param {Array=} targetActions - list of action names to be targeted for binding
* @returns {Object} boundServices - returns the new services object with the bounded action creators
*/
var bindWithDispatch = (dispatch, services, targetActions) => {
	const actions = targetActions || [
		"find",
		"get",
		"create",
		"update",
		"patch",
		"remove",
		"reset",
		"store",
		"authenticate",
		"logout"
	];
	const _services = {};
	Object.keys(services).forEach((_name) => {
		Object.keys(services[_name]).forEach((_method) => {
			if (actions.includes(_method)) {
				if (!_services[_name]) _services[_name] = {};
				_services[_name][_method] = bindActionCreators(services[_name][_method], dispatch);
			}
		});
	});
	return _services;
};
/**
* @param app - See reduxifyService
* @param serviceList - The feathers services to reduxify. See below.
* @param extraReducers - Extra reducers to add to the reducer. See below.
*/
var feathers_redux_default = (app, serviceList, extraReducers) => {
	const services = {};
	serviceList.forEach((serviceName) => {
		services[serviceName] = reduxifyService(app, serviceName, extraReducers?.[serviceName]);
	});
	return services;
};
//#endregion
//#region src/reducerUtils.ts
/**
* Replace query result data in Feathers service storage.
*
* @param state
* @param storeAs
* @param data
* @returns
*/
var replaceQueryResultData = (state, data, storeAs = "default") => {
	return state[storeAs] ? {
		...state,
		[storeAs]: {
			...state[storeAs],
			queryResult: {
				...state[storeAs].queryResult,
				data
			}
		}
	} : state;
};
/**
* Replace data in Feathers service storage.
*
* @param state
* @param storeAs
* @param data
* @returns
*/
var replaceData = (state, data, storeAs = "default") => {
	return state[storeAs] ? {
		...state,
		[storeAs]: {
			...state[storeAs],
			data
		}
	} : state;
};
//#endregion
//#region src/feathersjsConnect.jsx
function isFunction(functionToCheck) {
	return functionToCheck && {}.toString.call(functionToCheck) === "[object Function]";
}
/**
* @private
* @description Create a function if not already one
* @param {Function|Object|Array|String} Callable function or value of return for new function
*/
var createCallable = (f) => isFunction(f) ? f : () => f;
/**
* HOC that sends initial service.find() request for loading data
* and subscribes notification channel(s) for real time updates of Redux store.
* The function accepts either object { serviceName, options} or a function (store,props) => {} which
* must return the same object.
*
* @param {string} serviceName
* @param {object} options
* @param {string} options.sortBy The items retrieved by embedded find() function will be returned in unsorted order (as stored in DB).
*                 This option allows to specify which columns to use for sorting.
*                  ie. { last_name: 1, first_name: 1  }
*                  It consist of column name and 1/0 for ASC/DESC order.
* @param {string} options.where Allows to specify additional condition for find in form of an object with column names and values.
*                  ie. { where: { kit_id: 1 } }
*                  or when used with function like:
*                  (props) => ({ serviceName: "lesson", where: { kit_id: props.kitId }})
* @param {string} options.storeAs Allows to store and update data in alternative redux path instead of default state.feathers.service.data
* @param {string} options.saveToIndex data returned by find() are also stored in directly accessible index - state.feathers.service.default.searchIndex
*                  When using this option, it's one time request, data are stored in index, and no subscription is issued.
* @param {boolean} options.onlySubscribe does not request any data, but only subscribe for notifications (only POST /subscribe, no find())
* @param {boolean} options.doNotSubscribe does request the data, but do not subscribe for notifications (only find(), no POST /subscribe)
* @param {boolean} options.resetStoreOnUnmount reset the Redux store after the component unmounts to clear data
*
* @param {array|object} options.subscribeFor defines a condition(s) on which to subscribe to a service updates. The client will receive server events
*                  only for those matching the conditions.
*                  The condition could be simple:
*                      subscribeFor: { user_id: 1 }                // exact
*                      subscribeFor: { item_id: [1,2,3] }          // OR on single condition
*                      subscribeFor: { user_id: 1, item_id: 1 }    // AND on multiple conditions
*                  When condition has multiple properties, they are taken as logical AND.
*
*                  In case you need more complex subscriptions, enumerate them as an array:
*                      subscribeFor: [{ user_id: 1, item_id: 1 }, { user_id: 2 }]  // combining AND and OR conditions
*                  Items of array are taken as logical OR.
* @param {boolean} options.autoSubscribeToIds mutual exclusive with subscribeFor. When true, the client will auto-subscribe for IDs returned by the find() API call.
*                  This is useful for ie. paginated results when we want to receive updates only for items we actually received.
* 
*
* @example
*      1. feathersjsConnect("kit")
*       subscribes channel `kit`.
*
*      2. feathersjsConnect((props) => ({serviceName: "kit", options: { where: { id: props.kitId } } } ))
*       get all kits with condition based on props
*
*      3. feathersjsConnect(["kit", "lesson"])
*         feathersjsConnect([{ serviceName: "kit", options: {} }, { serviceName: "kit", options: {} }])
*       subscribe and get data to multiple services in one call
*
*      4. feathersjsConnect({ serviceName: "kit", options: { storeAs: "kits_custom" }})
*       subscribe for kit service and store data in state.feathers.kit.kits_custom location
* 
*  @param {import("./types").ReduxifiedServices} reduxifiedServices
*  @param {import("@feathersjs/feathers").Application} typedClient
*  @return {import("./types").FeathersConnect}
*/
var feathersjsConnect = (typedClient, reduxifiedServices) => {
	const subscribe = subscribeFn(typedClient);
	const unsubscribe = unsubscribeFn(typedClient);
	return (dataOrFn = [], options = {}) => (WrappedComponent) => {
		const debug = makeDebug(`feathersConnect:${WrappedComponent.displayName || WrappedComponent.name || "Component"}`);
		class FeathersJSConnect extends React.Component {
			static displayName = `FeathersJSConnect(${WrappedComponent.displayName || WrappedComponent.name || "Component"})`;
			_isMounted = false;
			static contextType = ReactReduxContext;
			store = this.context.store;
			prevData = this.createDataFromArguments(this.props, this.store);
			/** @type {Record<string, import("./subscriptions").FeathersConnectSubscribeOptions>} */
			subscribeOptions = {};
			/**
			* Take feathersjsConnect arguments and create array of data like:
			* [{ serviceName: "", options}]
			*
			* @param {*} props
			* @param {*} store
			* @returns {array} [{ serviceName: "", options}]
			*/
			createDataFromArguments(props, store) {
				let data = [];
				if (typeof dataOrFn === "string") data = [{
					serviceName: dataOrFn,
					options
				}];
				else if (Array.isArray(dataOrFn)) data = dataOrFn;
				else {
					data = createCallable(dataOrFn)(props, store.getState());
					if (data && !Array.isArray(data)) data = [data];
				}
				return data;
			}
			/**
			* callback function to create a subscribe options based on find() result data
			* @param {*} result
			*/
			autosubscribe = (serviceName, result) => {
				this.subscribeOptions[serviceName] = {
					subscribeFor: { id: result.data.map((item) => item.id) },
					overrideSubscription: true
				};
				subscribe(serviceName, this.subscribeOptions[serviceName]);
			};
			addSubscription = (data) => {
				const { dispatch } = this.store;
				const { serviceName, options = {} } = data;
				const { sortBy, where = {}, storeAs, saveToIndex, onlySubscribe, doNotSubscribe, autoSubscribeToIds, doNotReplaceExisting } = options;
				const { subscribeFor } = options;
				if (alreadyHasSubscription(serviceName, storeAs) && !this.subscribeOptions[serviceName]) {
					console.log(`There is already an active subscription on '${serviceName}". Request from component ${FeathersJSConnect.displayName} is cancelled.`);
					return;
				}
				if (!onlySubscribe) {
					const queryOptions = {
						...storeAs && { storeAs },
						...saveToIndex && { saveToIndex },
						...autoSubscribeToIds && { autoSubscribe: this.autosubscribe },
						...doNotReplaceExisting && { doNotReplaceExisting }
					};
					const query = {
						query: {
							...where,
							...sortBy && { $sort: sortBy }
						},
						meta: queryOptions
					};
					debug("dispatching %s.find() with params %o", serviceName, query);
					dispatch(reduxifiedServices[serviceName].find(query));
					if (doNotSubscribe) return;
				}
				if (saveToIndex) return;
				if (!autoSubscribeToIds) {
					this.subscribeOptions[serviceName] = {
						...subscribeFor && { subscribeFor },
						...storeAs && { storeAs }
					};
					subscribe(serviceName, this.subscribeOptions[serviceName]);
				}
			};
			removeSubscription = (data) => {
				const { dispatch } = this.store;
				const { serviceName, options = {} } = data;
				const { saveToIndex, storeAs, resetStoreOnUnmount } = options;
				if (saveToIndex) return;
				const isUnsubscribed = unsubscribe(serviceName, this.subscribeOptions[serviceName]);
				if (isUnsubscribed && storeAs) {
					debug("There are no listeners for service %s and `storeAs` was used. Reseting Redux store.", serviceName);
					dispatch(reduxifiedServices[serviceName].reset({ meta: { storeAs } }));
				}
				if (isUnsubscribed && resetStoreOnUnmount) {
					debug("There are no listeners for service %s and `resetStoreOnUnmount` was used. Reseting Redux store.", serviceName);
					dispatch(reduxifiedServices[serviceName].reset());
				}
			};
			componentDidUpdate() {
				const data = this.createDataFromArguments(this.props, this.store);
				if (!isEqual$1(data, this.prevData)) {
					const itemsToSubscribe = differenceWith(data, this.prevData, isEqual$1);
					differenceWith(this.prevData, data, isEqual$1).map((data) => this.removeSubscription(data));
					itemsToSubscribe.map((data) => this.addSubscription(data));
					this.prevData = data;
				}
			}
			componentDidMount() {
				this._isMounted = true;
				if (!this.prevData?.length) return;
				this.prevData.map((data) => this.addSubscription(data));
			}
			componentWillUnmount() {
				this._isMounted = false;
				if (!this.prevData?.length) return;
				this.prevData.map((data) => this.removeSubscription(data));
			}
			render() {
				return /* @__PURE__ */ jsx(WrappedComponent, { ...this.props });
			}
		}
		return FeathersJSConnect;
	};
};
//#endregion
//#region src/useFeathers.ts
var debug$1 = makeDebug("feather-client:useFeathers");
/**
* Take useFeathers arguments and create array of data like:
* [{ serviceName: "", options}]
*/
var createDataFromArguments = (hookArgs) => {
	let data = [];
	if (typeof hookArgs === "function") {
		data = hookArgs();
		if (!data) data = [];
	} else data = hookArgs;
	if (!Array.isArray(data)) data = [data];
	return data;
};
var createFeathersHook = (app, reduxifiedServices) => {
	const subscribe = subscribeFn(app);
	const unsubscribe = unsubscribeFn(app);
	return function useFeathers(hookArg, deps) {
		const store = useContext(ReactReduxContext).store;
		const prevData = useRef([]);
		const serviceSubscribeOptions = useRef({});
		const dataArg = createDataFromArguments(hookArg);
		/**
		* callback function to create a subscribe options based on find() result data
		* @param {*} result
		*/
		const autoSubscribe = useCallback((serviceName, result) => {
			const subscribeOptions = {
				subscribeFor: { id: result.data.map((item) => item.id) },
				overrideSubscription: true
			};
			serviceSubscribeOptions.current[serviceName] = subscribeOptions;
			subscribe(serviceName, subscribeOptions);
		}, []);
		const addSubscription = useCallback((data) => {
			const { dispatch } = store;
			const { serviceName, options = {} } = data;
			const { sortBy, where = {}, storeAs, onlySubscribe, saveToIndex, autoSubscribeToIds } = options;
			const { subscribeFor } = options;
			if (!onlySubscribe) {
				const queryOptions = {
					...storeAs && { storeAs },
					...saveToIndex && { saveToIndex },
					...autoSubscribeToIds && { autoSubscribe }
				};
				const query = {
					query: {
						...where,
						...sortBy && { $sort: sortBy }
					},
					meta: queryOptions
				};
				if (data.id !== void 0) {
					debug$1("dispatching %s.get(%d) with params %o", serviceName, data.id, query);
					dispatch(reduxifiedServices[serviceName].get(data.id, query));
				} else {
					debug$1("dispatching %s.$find() with params %o", serviceName, query);
					dispatch(reduxifiedServices[serviceName].find(query));
				}
			}
			if (subscribeFor) {
				const subscribeOptions = {
					subscribeFor,
					...storeAs && { storeAs }
				};
				serviceSubscribeOptions.current[serviceName] = subscribeOptions;
				subscribe(serviceName, subscribeOptions);
			}
		}, [autoSubscribe, store]);
		const removeSubscription = useCallback((data) => {
			const { dispatch } = store;
			const { serviceName, options = {} } = data;
			const { storeAs, resetStoreOnUnmount } = options;
			const subscribeOptions = serviceSubscribeOptions.current[serviceName];
			if (subscribeOptions) {
				if (unsubscribe(serviceName, subscribeOptions) && (storeAs || resetStoreOnUnmount)) {
					debug$1("There are no listeners for service %s. Resetting Redux store.", serviceName);
					dispatch(reduxifiedServices[serviceName].reset({ meta: { storeAs } }));
				}
			} else if (resetStoreOnUnmount) {
				debug$1("Resetting Redux store for service %s.", serviceName);
				dispatch(reduxifiedServices[serviceName].reset({ meta: { storeAs } }));
			}
		}, [store]);
		useEffect(() => {
			const props = dataArg;
			if (!isEqual$1(props, prevData.current)) {
				debug$1("useFeathers deps have changed, updating service subscriptions.");
				const itemsToSubscribe = differenceWith(props, prevData.current, isEqual$1);
				differenceWith(prevData.current, props, isEqual$1).map((data) => removeSubscription(data));
				itemsToSubscribe.map((data) => addSubscription(data));
				prevData.current = props;
			}
		}, deps || [dataArg]);
		useLayoutEffect(() => {
			return () => {
				debug$1("Cleaning up subscriptions.");
				if (!prevData.current?.length) return;
				prevData.current.map((data) => removeSubscription(data));
			};
		}, []);
	};
};
//#endregion
//#region src/listeners.ts
var debug = makeDebug("feathers-client:listeners");
var objectHasPropertyWithValue = (obj, obj2) => {
	return Object.entries(obj).every(([prop, value]) => {
		return Array.isArray(value) ? value.includes(obj2[prop]) : obj2[prop] === value;
	});
};
var isDataMatchingSubscriptions = (data, subscribeOption) => {
	const dataArray = Array.isArray(data) ? data : [data];
	const subscribeFor = subscribeOption.subscribeFor;
	return dataArray.some((item) => !subscribeFor || (Array.isArray(subscribeFor) ? subscribeFor.some((c) => objectHasPropertyWithValue(c, item)) : objectHasPropertyWithValue(subscribeFor, item)));
};
var createListener = (serviceName, serviceActionCreator, dispatch) => {
	return function(data) {
		const subscriptions = subscriptionMap.get(serviceName)?.options;
		if (subscriptions) subscriptions.forEach((subscription) => {
			if (isDataMatchingSubscriptions(data, subscription)) dispatch(serviceActionCreator(data, subscription));
		});
	};
};
/**
* Create custom service even listener. It will listen
* on event in websocket and then dispatch the event as Redux Action,
* which can be later handled in any Saga.
*/
var createCustomListener = (dispatch, serviceName, eventName) => {
	debug("Creating custom event listener '%s' for a service '%s'", eventName, serviceName);
	return createListener(serviceName, createAction(`@Feathers/${serviceName}/on_${eventName}`, prepareDataForCreator), dispatch);
};
/**
* Create and setup listeners for service default events (create/update/patch/remove).
*/
var setupDefaultListeners = (dispatch, serviceName, serviceActions, typedClient) => {
	const service = typedClient.service(serviceName);
	debug("[setupDefaultListeners] Adding default listeners service %s", serviceName);
	service.on("created", createListener(serviceName, serviceActions.onCreated, dispatch));
	service.on("updated", createListener(serviceName, serviceActions.onUpdated, dispatch));
	service.on("patched", createListener(serviceName, serviceActions.onPatched, dispatch));
	service.on("removed", createListener(serviceName, serviceActions.onRemoved, dispatch));
};
/**
* Setup a custom event listeners for a service.
*/
var setupCustomListeners = (dispatch, serviceName, customEvents, typedClient) => {
	const service = typedClient.service(serviceName);
	debug("[setupCustomListener] Adding custom listeners '%s' for service %s", customEvents, serviceName);
	for (const eventName of customEvents) service.on(eventName, createCustomListener(dispatch, serviceName, eventName));
};
//#endregion
//#region src/auth/connection.const.ts
var ConnectionStatus = /* @__PURE__ */ function(ConnectionStatus) {
	ConnectionStatus[ConnectionStatus["NOT_CONNECTED"] = 0] = "NOT_CONNECTED";
	ConnectionStatus[ConnectionStatus["NOT_AUTHENTICATED"] = 1] = "NOT_AUTHENTICATED";
	ConnectionStatus[ConnectionStatus["AUTHENTICATED"] = 2] = "AUTHENTICATED";
	ConnectionStatus[ConnectionStatus["NO_INTERNET"] = 3] = "NO_INTERNET";
	return ConnectionStatus;
}({});
var ConnectionStatusDesc = {
	[0]: "Not connected",
	[1]: "Connected, not authenticated",
	[2]: "Authenticated",
	[3]: "No internet"
};
var getStatusColor = (status) => {
	switch (status) {
		case 0: return "gray";
		case 1: return "#ff9933";
		case 2: return "#4dba16";
	}
};
var ConnectionMode = /* @__PURE__ */ function(ConnectionMode) {
	ConnectionMode["NONE"] = "none";
	ConnectionMode["WEBSOCKET"] = "websocket";
	ConnectionMode["POLLING"] = "polling";
	return ConnectionMode;
}({});
//#endregion
//#region src/auth/authSlice.ts
var authSlice = createSlice({
	name: "auth",
	initialState: {
		context: null,
		connection_status: ConnectionStatus.NOT_CONNECTED,
		connection_mode: ConnectionMode.NONE,
		isReauthenticating: true
	},
	reducers: {
		onSetAuth(state, { payload }) {
			if (payload.user) {
				const s4wAuthContext = payload;
				const full_name = s4wAuthContext.user.full_name || `${s4wAuthContext.user.first_name} ${s4wAuthContext.user.last_name}`;
				state.context = {
					...payload,
					name: full_name
				};
			} else state.context = payload;
			state.isReauthenticating = false;
		},
		onPatchAuth(state, { payload }) {
			if (state.context) state.context = {
				...state.context,
				...payload
			};
		},
		onResetAuth(state) {
			state.context = null;
			state.isReauthenticating = false;
		},
		onSetConnectionStatus(state, { payload }) {
			state.connection_status = payload.status;
			if (payload.mode) state.connection_mode = payload.mode;
		}
	}
});
var { onSetAuth, onResetAuth, onSetConnectionStatus, onPatchAuth } = authSlice.actions;
var AuthReducer = authSlice.reducer;
//#endregion
//#region src/auth/authSelectors.ts
/**
* @deprecated
*/
var authUser = (state) => state.auth.context;
var getAuthUserId = (state) => state.auth.context?.user.id;
var getAuthUserFbId = (state) => state.auth.context?.fbId;
var getAuthUserTenantId = ({ auth }) => auth.context?.tenant.id;
var getAuthUserTenantName = ({ auth }) => auth.context?.tenant.name;
var getAuthUserTenant = ({ auth }) => auth.context?.tenant;
var getAuthUserTenants = ({ auth }) => auth.context?.tenants;
var getAuthUser = (state) => state.auth.context;
var getAuthUserRoles = (state) => state.auth.context?.roles;
var isStudent = (state) => state.auth.context?.scope === "student";
var getIsTeacher = (state) => state.auth.context?.scope === "teacher";
var getAuthScope = (state) => state.auth.context?.scope;
var getConnectionStatus = (state) => state.auth.connection_status;
var getConnectionMode = (state) => state.auth.connection_mode;
var getTenantSettingsPageFormat = (state) => state.auth.context?.tenant.settings?.pageFormat || PageFormat.US_LETTER;
var getUniqueUserRoles = createSelector(getAuthUserRoles, (roles) => Array.isArray(roles) ? Array.from(new Set(roles.map((r) => r.role))) : []);
/**
* This function takes an object and it's type and return what permissions currently logged in
* user have to this object.
*
* This function check if the user has permissions "objectType:manage", "objectType:create", "objectType:read"
* "objectType:update", "objectType:delete" and return an object { canCreate: , canRead: , canUpdate: , canDelete:  }
*
* @param objectType "aKit" or "package" or "widget"
* @param object object which must have some properties checked by RBAC rules (ie. {kit_id:, package_id: tenant_id: })
*/
var getPermissions = memoize((objectType, object) => createSelector(getAuthUser, (authUser) => {
	const userRoles = authUser?.roles || [];
	const defaultPerm = {
		canCreate: false,
		canRead: false,
		canUpdate: false,
		canDelete: false,
		canReview: false
	};
	if (isPermitted({
		userRoles,
		action: `${objectType}:manage`,
		data: object
	})) return {
		canCreate: true,
		canRead: true,
		canUpdate: true,
		canDelete: true,
		canReview: true
	};
	if (isPermitted({
		userRoles,
		action: `${objectType}:create`,
		data: object
	})) defaultPerm.canCreate = true;
	if (isPermitted({
		userRoles,
		action: `${objectType}:read`,
		data: object
	})) defaultPerm.canRead = true;
	if (isPermitted({
		userRoles,
		action: `${objectType}:update`,
		data: object
	})) defaultPerm.canUpdate = true;
	if (isPermitted({
		userRoles,
		action: `${objectType}:delete`,
		data: object
	})) defaultPerm.canDelete = true;
	if (isPermitted({
		userRoles,
		action: `${objectType}:review`,
		data: object
	})) defaultPerm.canReview = true;
	return defaultPerm;
}));
/**
* Get all user's permissions for aKit
* @param object object which must have some properties checked by RBAC rules (ie. {kit_id:, package_id: tenant_id: })
* @returns KitPermissions
*/
var getKitPermissions = memoize((object) => {
	return createSelector(getPermissions("aKit", object), getAuthUser, (perm, authUser) => {
		const newPerm = { ...perm };
		const userRoles = authUser?.roles || [];
		if (isPermitted({
			userRoles,
			action: "aKit:review",
			data: object
		})) newPerm.canReview = true;
		if (isPermitted({
			userRoles,
			action: "aKit:publishRelease",
			data: object
		})) newPerm.canPublishRelease = true;
		if (isPermitted({
			userRoles,
			action: "aKit:publishPreview",
			data: object
		})) newPerm.canPublishPreview = true;
		return newPerm;
	});
});
var getAuthUserTenantsSorted = createSelector(getAuthUserTenants, (tenants) => [...tenants || []].sort(compareByNameCI));
//#endregion
//#region src/auth/hasPermission.ts
/**
* Reference to Redux Store.
*/
var reduxStoreRef = null;
var getReduxState = () => {
	return reduxStoreRef?.getState();
};
/**
* Initialize auth functions with Redux Store.
* @param reduxStore 
*/
var initializeAuthFunctions = (reduxStore) => {
	reduxStoreRef = reduxStore;
};
/**
* Determine if currently logged in user has permission to perform some action.
*
* Usage:
* 1. validate single action without params (only for static permissions)
*    `hasPermission("object:action")`
* 2.  validate multiple actions
*    `hasPermission(["object:action1", "object:action2"])`
* 3. validate complex permissions (dynamic) against provided data object
*     `hasPermission({ action: "object:action", data: { prop_to_validate: 1 }})`
*/
var hasPermission = (params) => {
	if (!reduxStoreRef) {
		console.error("Calling hasPermission without initialization. Call initializeAuthFunctions() after Redux store initialization.");
		return false;
	}
	const userRoles = getAuthUserRoles(reduxStoreRef.getState()) || [];
	if (typeof params === "string" || params instanceof String) return isPermitted({
		userRoles,
		action: String(params)
	});
	if (Array.isArray(params)) return isPermitted({
		userRoles,
		action: params
	});
	else {
		const { action, actionArray, data } = params;
		if (data) {
			const authObject = { ...data };
			if (!authObject.package_id && Array.isArray(data.packages)) {
				authObject.package_id = data.packages.map((p) => p.id);
				delete authObject.packages;
			}
			if (!authObject.kit_id && Array.isArray(data.kits)) {
				authObject.kit_id = data.kits.map((k) => k.id);
				delete authObject.kits;
			}
			return isPermitted({
				userRoles,
				action,
				actionArray,
				data: authObject
			});
		} else return isPermitted({
			userRoles,
			action,
			actionArray
		});
	}
};
//#endregion
//#region src/auth/propTypes.ts
var RolePropType = PropTypes.shape({
	id: PropTypes.number,
	role: PropTypes.oneOf(Object.values(ROLE)).isRequired,
	entity_id: PropTypes.string,
	entity_type: PropTypes.oneOf(Object.values(ROLE_ENTITY_TYPE)).isRequired
});
var UserRolesPropType = PropTypes.arrayOf(RolePropType.isRequired);
var AuthUserProps = {
	scope: PropTypes.string.isRequired,
	roles: UserRolesPropType.isRequired,
	name: PropTypes.string,
	secret: PropTypes.string,
	/** only in S4A */
	little: PropTypes.bool,
	/** only in S4W */
	fbId: PropTypes.string,
	user: PropTypes.shape({
		id: PropTypes.number.isRequired,
		email: PropTypes.string,
		first_name: PropTypes.string,
		last_name: PropTypes.string,
		full_name: PropTypes.string
	}).isRequired,
	tenant: PropTypes.shape({
		id: PropTypes.number.isRequired,
		name: PropTypes.string.isRequired,
		attributes: PropTypes.number
	}).isRequired,
	isImpersonating: PropTypes.bool,
	impersonator: PropTypes.shape({
		id: PropTypes.number.isRequired,
		email: PropTypes.string,
		first_name: PropTypes.string,
		last_name: PropTypes.string,
		full_name: PropTypes.string,
		userRoles: PropTypes.arrayOf(RolePropType)
	}),
	tenants: PropTypes.arrayOf(PropTypes.shape({
		id: PropTypes.number.isRequired,
		name: PropTypes.string.isRequired
	}).isRequired).isRequired,
	is_external: PropTypes.bool.isRequired
};
var AuthUserPropType = PropTypes.shape(AuthUserProps);
var PermissionsPropType = PropTypes.shape({
	canCreate: PropTypes.bool.isRequired,
	canRead: PropTypes.bool.isRequired,
	canUpdate: PropTypes.bool.isRequired,
	canDelete: PropTypes.bool.isRequired,
	canPublishRelease: PropTypes.bool,
	canPublishPreview: PropTypes.bool,
	canReview: PropTypes.bool
}).isRequired;
//#endregion
//#region src/auth/useAccessControl.tsx
var useAccessControl = () => {
	const userRoles = useSelector(getAuthUserRoles) || [];
	return { isPermitted: ({ action, actionArray, data }) => isPermitted({
		userRoles,
		action,
		actionArray,
		data
	}) };
};
//#endregion
//#region src/auth/AccessControl.tsx
var AccessControl = ({ action = "", actionArray, children, renderNoAccess, data }) => {
	const permitted = useAccessControl().isPermitted({
		action,
		actionArray,
		data
	});
	if (permitted && children) return children;
	if (!permitted && renderNoAccess) return typeof renderNoAccess === "function" ? renderNoAccess() : renderNoAccess;
	return null;
};
AccessControl.propTypes = {
	/** One or more permission to check, ie. "item:edit" or ["item:manage", "item:create"] */
	action: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
	/** DEPRECATED: use action property with either string or array of strings */
	actionArray: PropTypes.arrayOf(PropTypes.string),
	/** additional data which are used in dynamic role evaluation (see rbac-rules.js) */
	data: PropTypes.object,
	/** component or a function to render when user has no access */
	renderNoAccess: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
	/** Component children to be rendered */
	children: PropTypes.node
};
//#endregion
//#region src/auth/connectionStatus.ts
var isAlreadySetup = false;
/**
* Get the current transport mode of the socket.io connection.
*/
var getTransport = (feathersApp) => feathersApp.io.io.engine.transport.query.transport;
/**
* Setup listeners to update Redux state with connection status.
*/
var createOnlineIndicatorListeners = (feathersApp, dispatch, showError, hideError) => {
	if (isAlreadySetup) return;
	feathersApp.on(AUTH_EVENT.AUTHENTICATED, () => {
		dispatch(onSetConnectionStatus({ status: ConnectionStatus.AUTHENTICATED }));
		hideError?.();
	});
	feathersApp.on(AUTH_EVENT.LOGOUT, () => {
		dispatch(onSetConnectionStatus({ status: ConnectionStatus.NOT_AUTHENTICATED }));
	});
	feathersApp.on(AUTH_EVENT.NO_VALID_TOKEN, () => {
		feathersApp.io.close();
		dispatch(onResetAuth());
		showError?.("Authentication failed.", "Authentication session expired. Please log in again.");
	});
	feathersApp.on(AUTH_EVENT.REAUTHENTICATION_FAILED, () => {
		showError?.("Connection issue.", "There is an issue with network connection or SPORK server and the application could not be authenticated. Please restart the application.");
	});
	feathersApp.io.on("connect", () => {
		const connectionMode = getTransport(feathersApp);
		dispatch(onSetConnectionStatus({
			status: ConnectionStatus.NOT_AUTHENTICATED,
			mode: connectionMode
		}));
	});
	feathersApp.io.on("disconnect", () => {
		dispatch(onSetConnectionStatus({
			status: ConnectionStatus.NOT_CONNECTED,
			mode: ConnectionMode.NONE
		}));
	});
	isAlreadySetup = true;
};
//#endregion
//#region src/login/loginSelectors.ts
var loginSelectors_exports = /* @__PURE__ */ __exportAll({
	getError: () => getError,
	getIgnoreInfoMessage: () => getIgnoreInfoMessage,
	getMultipleTenants: () => getMultipleTenants,
	getProcessing: () => getProcessing,
	getSchool: () => getSchool,
	getToken: () => getToken,
	getUser: () => getUser,
	hasCredentials: () => hasCredentials,
	hasJustLoggedIn: () => hasJustLoggedIn
});
var getError = ({ login }) => login.error;
var getProcessing = ({ login }) => login.processing;
var getMultipleTenants = ({ login }) => login.multipleTenants?.map((t) => ({
	key: t.id,
	text: t.name,
	value: t.id
}));
var getIgnoreInfoMessage = ({ login }) => login.ignoreInfoMessage;
var hasJustLoggedIn = ({ login }) => login.hasJustLoggedIn;
var hasCredentials = ({ login }) => login.hasCredentials;
var getUser = ({ login }) => login.user;
var getSchool = ({ login }) => login.school;
var getToken = ({ login }) => login.token;
//#endregion
//#region src/login/loginSlice.ts
var initialState = {
	error: null,
	processing: false,
	multipleTenants: void 0,
	ignoreInfoMessage: false,
	hasJustLoggedIn: false,
	hasCredentials: false,
	user: null,
	school: null,
	token: null
};
var onLogin = createAction("Login/onLogin");
var onLogout = createAction("Login/onLogout");
var onLoginOAuth = createAction("Login/onLoginOAuth");
var onLoginWithMicrosoft = createAction("Login/onLoginWithMicrosoft");
var onProvision = createAction("Login/onProvision");
var onImpersonate = createAction("Login/onImpersonate");
var onEndImpersonate = createAction("Login/onEndImpersonate");
var onSwitchTenant = createAction("Login/onSwitchTenant");
var onReLogin = createAction("Login/onReLogin");
var onUserRoleChanged = createAction("Login/onUserRoleChanged");
var loginSlice = createSlice({
	name: "Login",
	initialState,
	reducers: {
		onLoginSuccess(state) {
			state.processing = false;
			state.hasJustLoggedIn = true;
			state.multipleTenants = void 0;
		},
		onLoginFail(state, { payload }) {
			state.processing = false;
			state.error = payload;
		},
		onLoginReset(state, { payload }) {
			state.error = null;
			state.processing = false;
			state.ignoreInfoMessage = payload;
			state.hasCredentials = false;
			state.hasJustLoggedIn = false;
		},
		onSelectTenant(state, { payload }) {
			state.processing = false;
			state.multipleTenants = payload;
		},
		onSetUserInfo(state, { payload }) {
			state.hasCredentials = true;
			state.processing = false;
			state.user = payload.user;
			state.school = payload.school;
			state.token = payload.token;
		},
		onConfirmUser(state) {
			state.processing = true;
		},
		onRedirectToOpenId(state) {
			state.processing = true;
		}
	},
	extraReducers(builder) {
		builder.addCase(onLogin, (state) => {
			state.processing = true;
		});
		builder.addCase(onProvision, (state) => {
			state.processing = true;
			state.hasCredentials = false;
		});
		builder.addCase(onImpersonate, (state) => {
			state.processing = true;
		});
		builder.addCase(onLoginWithMicrosoft, (state) => {
			state.processing = true;
		});
		builder.addCase(onReLogin, (state) => {
			state.token = null;
		});
	}
});
var { onLoginFail, onLoginReset, onLoginSuccess, onSelectTenant, onSetUserInfo, onConfirmUser, onRedirectToOpenId } = loginSlice.actions;
var LoginReducer = { login: loginSlice.reducer };
//#endregion
//#region src/feature/hasFeature.ts
/**
* Check the feature is active for current tenant and data matches the feature selectors
* Usage - see utils.test.js
*  featureCode - feature to check (see FeatureFlag constant)
*  activeFeatures - list of features active for TENANT of logged user
*  data - additional data for checking the feature is active in this case
*       - object where keys are appropriate constants for selectors - see FEATURE_SELECTOR_KEY constant
*       - e.g. { school_id: 6 } or { school_id:[6,7] } or { school_id:6, year_id:8 }
* @deprecated - use hasFeature() which does not require activeFeatures param.
*/
var isFeatureActive = (featureCode, activeFeatures, data = {}) => {
	if (featureCode && activeFeatures) {
		const feature = activeFeatures.find(({ code }) => code === featureCode);
		if (feature) return isDataMatchingSelector(feature.selectors, data);
	}
	return false;
};
/**
* Check if the feature is active for current tenant and data matches the feature selectors
*/
var hasFeature = (featureCode, data = {}) => {
	const reduxState = getReduxState();
	if (!reduxState) {
		console.error("Calling hasFeature without initialization. Call initializeAuthFunctions() after Redux store initialization.");
		return false;
	}
	return isFeatureActive(featureCode, feathersGetServiceData("feature_tenant")(reduxState), data);
};
//#endregion
//#region src/feature/propTypes.js
var featureCodePropType = PropTypes.oneOf(Object.values(FeatureFlag));
var activeFeaturesPropType = PropTypes.arrayOf(PropTypes.shape({
	code: featureCodePropType,
	selectors: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.number.isRequired).isRequired)
}).isRequired);
//#endregion
//#region src/feature/featureSelectors.ts
var getActiveFeatures = feathersGetServiceData("feature_tenant");
var getOciClassesFeatureFlag = createSelector(getActiveFeatures, (activeFeatures) => isFeatureActive(FeatureFlag.OCI_CLASSES, activeFeatures));
var isFiPEnabled = createSelector(getActiveFeatures, (activeFeatures) => isFeatureActive(FeatureFlag.FREE_IPLANNER, activeFeatures));
//#endregion
//#region src/feature/useFeatureFlag.ts
/**
* React hook to check if the feature is active for current tenant.
*/
var useFeatureFlag = () => {
	const userFeatures = useSelector(getActiveFeatures);
	return { isActive: (featureCode, data = {}) => {
		if (featureCode && userFeatures) {
			const feature = userFeatures.find(({ code }) => code === featureCode);
			if (feature) return isDataMatchingSelector(feature.selectors, data);
		}
		return false;
	} };
};
//#endregion
//#region src/feature/FeatureControl.tsx
/**
* This is a component managing access to part of application based on Feature flags settings.
*  usage example 
- for a feature switchable by tenant only:
```
<FeatureControl 
featureCode={FeatureFlag.STUDENT_JOIN_RULES} 
renderNoAccess={optional - component or a function to render when the feature is not accessible}
>
... content to be displayed according to feature settings
</FeatureControl>
```
- for a feature switchable by tenant and school:
```
<FeatureControl 
featureCode={FeatureFlag.VERSION_FOR_KIDS} 
data={{ school_id: rowData.id }}
renderNoAccess={optional - component or a function to render when the feature is not accessible}
>
... content to be displayed according to feature settings
</FeatureControl>
```
- for the case the functionality available for tenants having one of two features ON:
```
<FeatureControl 
featureCode={[FeatureFlag.COURSE_MENTORING, FeatureFlag.AUTHORING]} 
renderNoAccess={optional - component or a function to render when none of these features is ON}
>
... content to be displayed when one of these two features is ON
</FeatureControl>
```
**/
function FeatureControl({ featureCode, children, data, renderNoAccess = null, inverted = false }) {
	const featureFlag = useFeatureFlag();
	let isActive;
	if (Array.isArray(featureCode)) isActive = featureCode.some((feature) => featureFlag.isActive(feature, data));
	else isActive = featureFlag.isActive(featureCode, data);
	return isActive != inverted ? children || null : typeof renderNoAccess === "function" ? renderNoAccess() : renderNoAccess;
}
FeatureControl.propTypes = {
	/** Feature(s) to check */
	featureCode: PropTypes.oneOfType([featureCodePropType, PropTypes.arrayOf(featureCodePropType.isRequired)]).isRequired,
	/** Component children to be rendered */
	children: PropTypes.node,
	/** additional data necessary for the feature flag(s) evaluation e.g. school_id, year_id... */
	data: PropTypes.object,
	/** component or a function to render when the feature is not accessible */
	renderNoAccess: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
	/** invert feature flag logic - render children only when feature flag IS NOT present */
	inverted: PropTypes.bool
};
//#endregion
export { AUTH_EVENT, AccessControl, AuthReducer, AuthUserPropType, AuthUserProps, ConnectionMode, ConnectionStatus, ConnectionStatusDesc, FeatureControl, JwtAuthenticationClient, LoginReducer, loginSelectors_exports as LoginSelectors, PermissionsPropType, RolePropType, UserRolesPropType, activeFeaturesPropType, addIdToSubscriptionFn, authUser, bindWithDispatch, configureAuthentication, createConnection, createFeathersHook, createOnlineIndicatorListeners, createRestClient, feathersGetQueryResult, feathersGetSearchIndexData, feathersGetServiceData, feathersGetSingleData, feathersIsError, feathersIsLoading, feathersIsLoadingCustom, feathersIsLoadingMultiple, feathersIsSaving, feathersjsConnect as feathersjsConnectFn, featureCodePropType, getActiveFeatures, getAuthScope, getAuthUser, getAuthUserFbId, getAuthUserId, getAuthUserRoles, getAuthUserTenant, getAuthUserTenantId, getAuthUserTenantName, getAuthUserTenants, getAuthUserTenantsSorted, getConnectionMode, getConnectionStatus, getFeathersState, getIsTeacher, getKitPermissions, getOciClassesFeatureFlag, getPermissions, getReduxState, getStatusColor, getTenantSettingsPageFormat, getUniqueUserRoles, hasFeature, hasPermission, initializeAuthFunctions, isFeatureActive, isFiPEnabled, isStudent, normalizeOptions, onConfirmUser, onEndImpersonate, onImpersonate, onLogin, onLoginFail, onLoginOAuth, onLoginReset, onLoginSuccess, onLoginWithMicrosoft, onLogout, onPatchAuth, onProvision, onReLogin, onRedirectToOpenId, onResetAuth, onSelectTenant, onSetAuth, onSetConnectionStatus, onSetUserInfo, onSwitchTenant, onUserRoleChanged, prepareDataForCreator, feathers_redux_default as reduxifyServices, replaceData, replaceQueryResultData, sanitizeData as sanitizeReduxifiedData, setupCustomListeners, setupDefaultListeners, setupResubscription, subscribeFn, subscriptionMap, unsubscribeAllFn, unsubscribeFn, useFeatureFlag };

//# sourceMappingURL=index.mjs.map